import ContactAvatar from '../components/ContactAvatar.tsx';
import { extractProviderMessageId } from '../utils/sentMessage.ts';
import ContactAttendance, { type AssignmentFilter, useAttendanceEnabled, type ConversationState } from '../components/Chat/ContactAttendance.tsx';
// ChatPage5.tsx - Versão otimizada e atualizada
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  ImageIcon, Video as VideoIcon,
  Target,
  Calendar,
  CheckCircle2,
  DollarSign,
  MessageSquare,
  HeartHandshake,
  ChevronLeft,
  Headphones,
  Tag as TagIcon,
  Timer,
  PanelRightOpen,
  X,
  Loader2,
} from 'lucide-react';
import { useIsMobile } from '../hooks/useIsMobile.ts';
import ChatMobile from './ChatMobile.tsx';
import api, {
  getContacts,
  getContactsNoHistory,
  getContactMode,
  markContactAsRead,
  sendWhatsAppText,
  sendWhatsAppAudio,
  sendWhatsAppAudioDirect,
  sendWhatsAppImage,
  sendWhatsAppVideo,
  reactToWhatsAppMessage,
  sendNPS,
  SendImageParams,
  SendAudioParams,
  SendVideoParams,
  getClinicInfo,
  unifiedWebSocketManager,
  OptimizedMessage,
  get24hWindowStatus,
  ConversationWindowStatus,
  sendOfficialWhatsAppText,
  sendOfficialWhatsAppImage,
  sendOfficialWhatsAppAudio,
  sendOfficialWhatsAppVideo,
  uploadMediaForOfficial,
  getClinicWhatsAppProviders,
  WhatsAppProviders,
  formatBrazilianPhone,
  parseApiError,
  type ContactModeResponse,
  type ParsedApiError
} from '../services/api.ts';
import { conversationKey, conversationEventKey, conversationContactId } from '../utils/conversationIdentity.ts';
import { ContactMessageDeliveryInbox, contactDeliveryScope, mergeContactMessageDelivery, preserveCurrentContactMessage } from '../utils/contactMessageDelivery.ts';
import { useInstagramConversation } from '../hooks/useInstagramConversation.ts';
import { instagramContactDisplay } from '../utils/instagramContactDisplay.ts';
import { useInstagramComments } from '../hooks/useInstagramComments.ts';
import { InstagramCommentComposer } from '../components/Chat/InstagramCommentComposer.tsx';
import { markInstagramRead } from '../services/instagram.ts';
import { formatChatTimestamp } from '../utils/date.ts';
import { formatLastMessage } from '../utils/lastMessagePreview.ts';
import { ConnectionStatusIndicator } from '../components/ConnectionStatusIndicator.tsx';
import { VirtualizedMessageList } from '../components/VirtualizedMessageList.tsx';
import { OptimizedMessageContent } from '../components/OptimizedMessageContent.tsx';
import { OptimizedMessageInput } from '../components/OptimizedMessageInput.tsx';
import { useOptimizedMessages } from '../hooks/useOptimizedMessages.tsx';
import { DateSeparator } from '../components/DateSeparator.tsx';
import { groupMessagesByDay, MessageGroup } from '../utils/messageGrouping.ts';
import AudioWaveform from '../components/AudioWaveform.tsx';
import { ContactFilters } from '../components/ContactFilters.tsx';
import { archiveContact, unarchiveContact } from '../services/api.ts';
import { Archive, ArchiveRestore } from 'lucide-react';
import { TagsService, TagResponse } from '../services/apiTags.ts';
import ContactSidebar, { ContactSidebarTab } from '../components/ContactSidebar.tsx';
import TagSelector from '../components/TagSelector.tsx';
import ChatHeader from '../components/Chat/ChatHeader.tsx';
import { resolveTemporaryContactName } from '../components/Chat/contactIdentity.ts';
import {
  isChatContactIdentityResolved,
  pickNavigationContact,
  readChatNavigationPayload,
  type ChatNavigationPayload,
} from '../components/Chat/chatNavigationIdentity.ts';
import { ContactPresenceBubble } from '../components/Chat/ContactPresence.tsx';
import ChatSidebar from '../components/Chat/ChatSidebar.tsx';
import ContactModeControl, {
  applyContactModeToCollection,
  classifyContactModeEvent,
  registerContactModeVersion,
  resolvePolledContactMode,
  withKnownContactMode,
} from '../components/Chat/ContactModeControl.tsx';
import type { ContactModeVersion } from '../components/Chat/ContactModeControl.tsx';
import { ProviderSelectionModal } from '../components/ProviderSelectionModal.tsx';
import { TemplateSendModal } from '../components/TemplateSendModal.tsx';
import WhatsAppIdentityErrorAlert from '../components/WhatsAppIdentityErrorAlert.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { useContactPresence } from '../hooks/useContactPresence.ts';
import { hasPermission } from '../services/permissionService.ts';
import { activeClinicId } from '../services/agentLearning.ts';
import { AgentCorrectionDialog } from '../components/AgentLearning/AgentCorrectionDialog.tsx';


// Tipos Contact e FlowProgress extraídos para components/Chat/types.ts
import type { Contact, FlowProgress } from '../components/Chat/types.ts';

const isInstagramContact = (contact: { active_channel?: string | null; source_channel?: string | null } | null | undefined): boolean =>
  (contact?.active_channel || contact?.source_channel) === 'instagram';

const realContactPhone = (contact: { phone?: string | null; active_channel?: string | null; source_channel?: string | null }): string =>
  isInstagramContact(contact) ? '' : String(contact.phone || '').replace(/\D/g, '');

const instagramDeliveryStatus = (ack: unknown): Contact['lastMessageStatus'] => {
  if (typeof ack !== 'number') return undefined;
  return ({ '-1': 'failed', '0': 'sending', '1': 'sent', '2': 'delivered', '3': 'read' } as Record<string, Contact['lastMessageStatus']>)[String(ack)];
};

const CONTACTS_PAGE_SIZE = 50;
const CONTACTS_FILTER_DEBOUNCE_MS = 300;

const normalizeContactSearchTerm = (term: string): string => {
  const trimmedTerm = term.trim();
  if (!trimmedTerm) return '';

  const digitsOnly = trimmedTerm.replace(/\D/g, '');
  const hasLetters = /[A-Za-zÀ-ÿ]/.test(trimmedTerm);

  return digitsOnly.length >= 8 && !hasLetters ? digitsOnly : trimmedTerm;
};

const persistedWhatsAppContactId = (...values: unknown[]): number => {
  for (const value of values) {
    const id = Number(value);
    if (Number.isInteger(id) && id > 0) return id;
  }
  return 0;
};

const getWhatsAppIdentityVariants = (phone: string): Set<string> => {
  let normalized = String(phone || '').replace(/\D/g, '');
  if (normalized.length === 10 || normalized.length === 11) {
    normalized = `55${normalized}`;
  }

  const variants = new Set<string>();
  if (!normalized) return variants;
  variants.add(normalized);

  if (normalized.startsWith('55') && (normalized.length === 12 || normalized.length === 13)) {
    const prefix = normalized.slice(0, 4);
    const localNumber = normalized.slice(4);
    if (localNumber.length === 8 && '6789'.includes(localNumber[0])) {
      variants.add(`${prefix}9${localNumber}`);
    } else if (localNumber.length === 9 && localNumber.startsWith('9')) {
      variants.add(`${prefix}${localNumber.slice(1)}`);
    }
  }

  return variants;
};

const getStoredContactFunnelStages = (): string[] | null => {
  const rawStages = localStorage.getItem('contact_funnel_stages');
  if (!rawStages) return null;

  try {
    const parsedStages = JSON.parse(rawStages);
    if (!Array.isArray(parsedStages)) return null;

    return parsedStages
      .map(stage => String(stage).trim().toLowerCase())
      .filter(Boolean);
  } catch (error) {
    console.error('Erro ao ler escopo de contatos da equipe:', error);
    return null;
  }
};

const hasRestrictedContactScope = (): boolean => {
  const stages = getStoredContactFunnelStages();
  return Boolean(stages && !stages.includes('all'));
};

const getRestrictedContactStages = (): string[] => {
  const stages = getStoredContactFunnelStages();
  if (!stages || stages.includes('all')) return [];
  return stages;
};

const getInitialContactFilters = (): ContactFilters => {
  const restrictedStages = getRestrictedContactStages();
  if (restrictedStages.length > 0) {
    return {
      funnelStages: restrictedStages,
      activeFlows: []
    };
  }

  return {
    funnelStages: [],
    activeFlows: []
  };
};

const ChatPage5: React.FC = () => {
  const attendanceClaimTarget = useRef<HTMLDivElement>(null);
  const attendanceCloseTarget = useRef<HTMLDivElement>(null);
  // Hook para detectar se é mobile - DEVE vir antes de qualquer condicional
  const isMobile = useIsMobile(768);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Estados - TODOS os hooks devem ser declarados antes de qualquer return condicional
  const attendanceEnabled = useAttendanceEnabled();
  const [conversationState, setConversationState] = useState<ConversationState>('open');
  const [attendanceRevision, setAttendanceRevision] = useState(0);
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>('all');
  const [contacts, setContacts] = useState<Map<string, Contact>>(new Map());
  const contactDeliveryInbox = useRef(new ContactMessageDeliveryInbox());
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const selectedConversationRef = useRef('');
  const instagramAudioFilesRef = useRef(new WeakMap<Blob, File>());
  selectedConversationRef.current = conversationKey(selectedContact);

  useEffect(() => {
    if (!attendanceEnabled) return;
    return unifiedWebSocketManager.onMessage('*', (event: any) => {
      if ((event.type || event.data?.type) !== 'contact_attendance_invalidated') return;
      contactsRequestIdRef.current += 1;
      contactsRef.current = new Map();
      setContacts(new Map());
      setAttendanceRevision(value => value + 1);
      window.dispatchEvent(new Event('contact-attendance-changed'));
      if (selectedContact?.id) {
        const id = selectedContact.id;
        api.get(`/api/contacts/${id}/attendance`, { params: { clinic_id: localStorage.getItem('clinic_id') } }).then(response => {
          if (response.data.enabled) setSelectedContact(current => current?.id === id ? { ...current, human_mode: response.data.human_mode === true } : current);
        }).catch((error: any) => {
          if ([403, 404].includes(error.response?.status)) setSelectedContact(current => current?.id === id ? null : current);
        });
      }
    });
  }, [attendanceEnabled, selectedContact?.id]);
  const [hasMoreContacts, setHasMoreContacts] = useState(true);
  const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isThrottled, setIsThrottled] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [replyTarget, setReplyTarget] = useState<OptimizedMessage | null>(null);
  const [clinicInfo, setClinicInfo] = useState<{ name: string; logo_url: string | null }>({
    name: 'Clínica',
    logo_url: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isContactsLoading, setIsContactsLoading] = useState(true);
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(false);
  // Estado para controlar a navegação mobile (lista ou chat)
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [contactFlowStatus, setContactFlowStatus] = useState<{ [key: string]: any }>({});
  const [isLoadingFlowStatus, setIsLoadingFlowStatus] = useState(false);
  const [showContactSidebar, setShowContactSidebar] = useState(false);
  const [contactSidebarTab, setContactSidebarTab] = useState<ContactSidebarTab>('timeline');
  const [timelineRefreshToken, setTimelineRefreshToken] = useState(0);

  const [isContactScopeRestricted, setIsContactScopeRestricted] = useState(hasRestrictedContactScope);

  // Estados para filtros e arquivamento
  const [contactFilters, setContactFilters] = useState<ContactFilters>(getInitialContactFilters);
  const [showArchivedSection, setShowArchivedSection] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Estados para Tags do Contato
  const [contactTags, setContactTags] = useState<TagResponse[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Estado para Janela de 24h do WhatsApp Oficial
  const [windowStatus, setWindowStatus] = useState<ConversationWindowStatus | null>(null);

  // Estados para seleção de provider (WAHA vs API Oficial)
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<{
    type: 'text' | 'audio' | 'image' | 'video';
    content: any;
    localMessageId: string;
    duration?: number;  // ✅ Para áudio: duração em segundos
    replyTarget?: OptimizedMessage | null;
  } | null>(null);
  const [whatsappProviders, setWhatsappProviders] = useState<WhatsAppProviders | null>(null);
  const [sendError, setSendError] = useState<ParsedApiError | null>(null);
  const [correctionMessage, setCorrectionMessage] = useState<OptimizedMessage | null>(null);
  const [correctionContactId, setCorrectionContactId] = useState<number | null>(null);
  const [correctionClinicId, setCorrectionClinicId] = useState<number | null>(null);
  const [correctionPublished, setCorrectionPublished] = useState(false);
  const [isResolvingSelectedContact, setIsResolvingSelectedContact] = useState(false);
  const [pendingChatNavigation, setPendingChatNavigation] = useState<ChatNavigationPayload | null>(null);
  const pendingRouteNavigationRef = useRef<ChatNavigationPayload | null>(null);

  // Estado para modal de envio de templates (fora da janela de 24h)
  const [showTemplateSendModal, setShowTemplateSendModal] = useState(false);

  const selectedContactId = Number(selectedContact?.id || 0);
  const incomingChatNavigation = readChatNavigationPayload(location.state);
  const hasPendingContactNavigation =
    pendingChatNavigation !== null || incomingChatNavigation !== null;
  const isSelectedContactIdentityResolved = isChatContactIdentityResolved({
    selectedContactId,
    hasPendingNavigation: hasPendingContactNavigation,
  });

  const requireSelectedContactId = React.useCallback((): number | null => {
    const contactId = Number(selectedContact?.id || 0);
    if (
      !hasPendingContactNavigation &&
      Number.isInteger(contactId) &&
      contactId > 0
    ) {
      return contactId;
    }

    setSendError({
      code: 'contact_identity_pending',
      message: 'Este contato ainda está sendo identificado. Aguarde a sincronização ou revise os cadastros antes de enviar.',
      existingPhone: selectedContact?.phone,
    });
    return null;
  }, [hasPendingContactNavigation, selectedContact]);


  // Funções para gerenciar tags do contato
  const loadContactTags = React.useCallback(async (contact: Contact) => {
    if (!contact) {
      console.log('❌ loadContactTags - Contact é null/undefined');
      return;
    }

    try {
      setTagsLoading(true);
      console.log(`🏷️ Carregando tags do contato: ${contact.phone} (ID: ${contact.id})`);

      // 🔍 LOG DIAGNÓSTICO: Verificar estrutura completa do contato
      console.log('🔍 loadContactTags - Estrutura completa do contato:', {
        id: contact.id,
        name: contact.name,
        phone: contact.phone,
        tags: contact.tags,
        tagsLength: contact.tags?.length || 0,
        tagsType: typeof contact.tags,
        isArray: Array.isArray(contact.tags),
        contactObjectKeys: Object.keys(contact)
      });

      // Primeiro, vamos tentar usar as tags já disponíveis no objeto Contact
      if (contact.tags && Array.isArray(contact.tags) && contact.tags.length > 0) {
        setContactTags(contact.tags);
        console.log(`✅ Tags encontradas no objeto Contact: ${contact.tags.length}`,
          contact.tags.map(t => ({ id: t.id, name: t.name, color: t.color }))
        );
      } else {
        console.log('🔄 Tags não encontradas no objeto Contact, buscando da API...');
        // Buscamos da API usando o ID do contato
        const response = await TagsService.getContactTags(contact.id);
        console.log('🔍 Resposta da API TagsService.getContactTags:', {
          contactId: contact.id,
          response,
          tags: response.tags,
          tagsLength: response.tags?.length || 0,
          isArray: Array.isArray(response.tags)
        });
        setContactTags(response.tags);
        console.log(`✅ Tags carregadas da API: ${response.tags.length}`,
          response.tags.map(t => ({ id: t.id, name: t.name, color: t.color }))
        );
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tags do contato:', error);
      console.error('❌ Stack do erro:', (error as any)?.stack);
      setContactTags([]); // Define array vazio em caso de erro
    } finally {
      setTagsLoading(false);
    }
  }, []);

  const updateContactTags = React.useCallback(async (tagIds: number[]) => {
    if (!selectedContact) {
      console.error('❌ updateContactTags: selectedContact é null/undefined');
      return;
    }

    const contactId = selectedContact.id;
    console.log('🔍 DEBUG - selectedContact completo:', selectedContact);
    console.log('🔍 DEBUG - contactId:', contactId, 'tipo:', typeof contactId);

    if (!contactId || contactId <= 0 || typeof contactId !== 'number') {
      console.error(`❌ updateContactTags: ID inválido - valor: ${contactId}, tipo: ${typeof contactId}`);

      // Feedback para usuário
      alert('Não é possível gerenciar tags para este contato. Contato ainda não foi salvo no sistema.');
      return;
    }

    try {
      console.log(`🏷️ Atualizando tags do contato ${selectedContact.phone} (ID: ${contactId}): ${tagIds.join(', ')}`);

      // 1. Atualização Otimista (Optimistic Update)
      // Encontrar os objetos de tag completos baseados nos IDs
      // Precisamos ter acesso a todas as tags para isso, mas como fallback podemos manter os IDs se necessário
      // ou apenas filtrar as tags atuais se for remoção.
      // Para adição, o ideal seria ter a lista completa de tags disponível.

      // Como não temos 'allTags' aqui facilmente, vamos fazer uma atualização parcial
      // Se for remoção, é fácil filtrar. Se for adição, precisamos esperar o reload ou passar o objeto tag completo.
      // Mas o TagSelector já passa os IDs atualizados.

      // Vamos forçar um estado temporário se possível, ou confiar no reload rápido.
      // O problema relatado é que precisa de F5, o que implica que loadContactTags não está funcionando ou não está sendo chamado.

      await TagsService.updateContactTags(contactId, tagIds);

      // 2. Buscar tags frescas da API (não usar loadContactTags porque ele
      // reusa selectedContact.tags em cache, que ainda tem a tag removida).
      const fresh = await TagsService.getContactTags(contactId);
      const freshTags = fresh.tags || [];
      console.log(`✅ Tags frescas da API após update: ${freshTags.length}`, freshTags);

      setContactTags(freshTags);

      // 3. Sincronizar selectedContact.tags e a lista de contatos
      setSelectedContact(prev => prev ? { ...prev, tags: freshTags } : prev);
      setContacts(prev => {
        const newContacts = new Map(prev);
        const contactToUpdate = newContacts.get(String(contactId));
        if (contactToUpdate) {
          newContacts.set(String(contactId), { ...contactToUpdate, tags: freshTags });
        }
        return newContacts;
      });

      console.log('✅ Tags atualizadas com sucesso');
    } catch (error: any) {
      console.error('❌ Erro ao atualizar tags:', error);

      // Feedback específico para erro 422
      if (error.response?.status === 422) {
        alert('Erro de validação ao atualizar tags. Verifique se o contato está salvo corretamente.');
      }
    }
  }, [selectedContact]);

  // Ref para rastrear mensagens já processadas e evitar duplicação
  const processedWebSocketMessages = useRef<Set<string>>(new Set());
  const contactModeVersionRef = useRef<Map<number, ContactModeVersion>>(new Map());
  const contactModeValueRef = useRef<Map<number, boolean>>(new Map());
  const contactModeReconcileGenerationRef = useRef<Map<number, number>>(new Map());
  const contactModeReconcileInFlightRef = useRef<Map<number, Promise<void>>>(new Map());
  const getTotalUnreadCount = (): number => {
    let total = 0;
    contacts.forEach((contact) => {
      total += contact.unreadCount || 0;
    });
    return total;
  };

  // StatusTag e SidebarTags foram extraídos para components/Chat/

  // Hook otimizado para gerenciar mensagens com cache e paginação
  const {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMoreMessages,
    updateMessageTracking,
    sendMessage
  } = useOptimizedMessages(
    conversationKey(selectedContact) || null,
    { pageSize: 50 }
  );

  const instagramConversation = useInstagramConversation(
    isInstagramContact(selectedContact) && isSelectedContactIdentityResolved ? selectedContactId : null,
  );
  const selectedIsInstagram = isInstagramContact(selectedContact);
  const selectedContactDisplay = selectedContact ? instagramContactDisplay(selectedContact, instagramConversation.identity) : null;
  const displayContacts = useMemo(() => new Map(Array.from(contacts.entries()).map(([key, contact]) =>
    [key, instagramContactDisplay(contact, instagramConversation.identity)])), [contacts, instagramConversation.identity]);
  const instagramReady = !!instagramConversation.identity && !instagramConversation.loading && !instagramConversation.sending;
  const instagramWindowOpen = Date.parse(instagramConversation.identity?.window_expires_at || '') > Date.now();
  const instagramCanSendText = instagramReady && instagramWindowOpen && !!instagramConversation.identity?.capabilities.send_text;
  const instagramCanSendMedia = instagramReady && instagramWindowOpen && !!instagramConversation.identity?.capabilities.upload_private_media;
  const instagramComments = useInstagramComments(selectedIsInstagram ? selectedContactId : null,
    instagramConversation.identity, result => {
      if (result.message_id && result.content) sendMessage(result.content, 'comment', String(result.message_id), 'instagram', {
        status: 'sent', providerMessageId: result.provider_message_id ? `ig-comment:${result.provider_message_id}` : null,
      });
    });


  const contactPresence = useContactPresence({
    contactId: isSelectedContactIdentityResolved ? selectedContactId : null,
    phone: selectedContact && !isInstagramContact(selectedContact) ? selectedContact.phone : null,
    enabled: isSelectedContactIdentityResolved && !isInstagramContact(selectedContact),
  });

  const getReplyPreviewContent = (message: OptimizedMessage): string => {
    const content = message.content as any;
    if (message.type === 'location') {
      const rawContent = typeof content === 'string' ? content : JSON.stringify(content || {});
      return formatLastMessage(rawContent, 'location').text;
    }
    if (typeof content === 'string') return content;
    if (!content) return `[${message.type}]`;
    return (
      content.caption ||
      content.message ||
      content.url ||
      content.audioUrl ||
      content.videoUrl ||
      content.imageUrl ||
      content.nps_data?.question ||
      `[${message.type}]`
    );
  };

  const normalizeSenderDisplayName = (value?: string | null): string | null => {
    const cleaned = (value || '').trim();
    if (!cleaned || cleaned.toLowerCase() === 'unknown' || cleaned.toLowerCase() === 'desconhecido') {
      return null;
    }
    return cleaned;
  };

  const getMessageSenderLabel = (message: OptimizedMessage): string => {
    if (message.fromMe) {
      return normalizeSenderDisplayName(clinicInfo.name) || 'Conta WhatsApp';
    }
    return normalizeSenderDisplayName(message.sender?.name) || 'Contato';
  };

  const buildReplyMetadata = (message: OptimizedMessage | null): OptimizedMessage['replyTo'] | undefined => {
    if (!message) return undefined;
    return {
      providerMessageId: message.providerMessageId || String(message.id),
      messageId: message.id,
      content: getReplyPreviewContent(message),
      type: message.type,
      senderName: getMessageSenderLabel(message)
    };
  };

  const applySentLastMessageMetadata = (
    contact: Contact,
    {
      providerMessageId,
      sourceChannel = 'waha',
      status = 'sent',
      messageType = 'text'
    }: {
      providerMessageId?: string | null;
      sourceChannel?: 'waha' | 'official_whatsapp' | 'instagram';
      status?: Contact['lastMessageStatus'];
      messageType?: string;
    } = {}
  ) => {
    const incoming: Contact = {
      ...contact,
      lastMessageStatus: status,
      lastMessageType: messageType,
      lastMessageFromMe: true,
      lastMessageProviderId: providerMessageId || null,
      lastMessageSourceChannel: sourceChannel,
      lastMessageDeliveryError: undefined,
      lastMessageDeliveryStatusAt: undefined,
      active_channel: sourceChannel || contact.active_channel,
    };
    Object.assign(contact, contactDeliveryInbox.current.apply(contactDeliveryScope(), conversationKey(contact), {
      ...incoming, ...mergeContactMessageDelivery(contact, incoming),
    }));
  };


  useEffect(() => {
    setReplyTarget(null);
    setSendError(null);
    setShowProviderModal(false);
    setPendingMessage(null);
    setShowTemplateSendModal(false);
  }, [selectedContact?.id, selectedContact?.phone, selectedContact?.active_channel]);

  // Estados para modal de contatos sem histórico
  const [modalContacts, setModalContacts] = useState<Contact[]>([]);
  const [isLoadingModalContacts, setIsLoadingModalContacts] = useState(false);
  const [modalOffset, setModalOffset] = useState(0);
  const [hasMoreModalContacts, setHasMoreModalContacts] = useState(true);

  // Função para carregar contatos sem histórico no modal
  const loadModalContacts = async (offset = 0, limit = 20, append = false, search = '') => {
    console.log('📥 loadModalContacts chamada:', {
      offset,
      limit,
      append,
      search,
      modalSearchTerm
    });

    setIsLoadingModalContacts(true);

    try {
      // Use o parâmetro search se fornecido, senão use o estado atual
      const searchTerm = search !== undefined ? search : modalSearchTerm;

      const response = await getContactsNoHistory({
        limit,
        offset,
        search: searchTerm
      });

      // Processar lastMessage dos contatos do modal também
      const processedContacts = response.contacts.map(c => {
        const processedLastMessage = c.lastMessage || '';

        return withKnownContactMode({
          ...c,
          lastMessage: processedLastMessage
        }, contactModeValueRef.current);
      });

      console.log('📋 Resposta da API de contatos sem histórico:', {
        contactsCount: processedContacts.length,
        total: response.total,
        hasMore: response.has_more,
        searchUsed: searchTerm
      });

      if (append) {
        setModalContacts(prev => [...prev, ...processedContacts]);
      } else {
        setModalContacts(processedContacts);
      }

      setHasMoreModalContacts(response.has_more);
      setModalOffset(append ? offset + response.contacts.length : response.contacts.length);

      console.log('✅ Contatos do modal atualizados:', {
        total: response.contacts.length,
        hasMore: response.has_more,
        currentOffset: append ? offset + response.contacts.length : response.contacts.length,
        append
      });
    } catch (error) {
      console.error('❌ Erro ao carregar contatos do modal:', error);
    } finally {
      setIsLoadingModalContacts(false);
    }
  };

  // Carregar contatos do modal quando o modal abrir (executa UMA vez)
  useEffect(() => {
    if (showNewChatModal) {
      console.log('🔥 Abrindo modal - carregando contatos iniciais');
      // Reset estados do modal
      setModalContacts([]);
      setModalOffset(0);
      setModalSearchTerm('');
      setHasMoreModalContacts(true);
      // Carregar contatos iniciais sem busca
      setTimeout(() => {
        loadModalContacts(0, 20, false, '');
      }, 100); // Pequeno delay para garantir que os estados foram atualizados
    }
  }, [showNewChatModal]);

  // Recarregar contatos do modal quando o termo de busca mudar (com debounce)
  useEffect(() => {
    // Só executa se o modal estiver aberto E se não for o primeiro carregamento (modalSearchTerm não é vazio)
    if (showNewChatModal && modalSearchTerm !== '') {
      console.log('🔍 Termo de busca mudou no modal:', modalSearchTerm);
      const timeoutId = setTimeout(() => {
        setModalOffset(0);
        setHasMoreModalContacts(true);
        loadModalContacts(0, 20, false, modalSearchTerm);
      }, 300); // Debounce de 300ms

      return () => clearTimeout(timeoutId);
    } else if (showNewChatModal && modalSearchTerm === '' && modalContacts.length > 0) {
      // Se o campo de busca foi limpo, recarregar todos os contatos
      console.log('🔍 Campo de busca limpo - recarregando todos os contatos');
      setModalOffset(0);
      setHasMoreModalContacts(true);
      loadModalContacts(0, 20, false, '');
    }
  }, [modalSearchTerm]);

  // 📱 Monitorar mudanças no contato selecionado para atualizar status da janela 24h
  useEffect(() => {
    // Carregar windowStatus se:
    // 1. O canal ativo já é official_whatsapp OU
    // 2. A clínica tem Official API configurada (para permitir envio via modal)
    let disposed = false;
    const shouldLoadWindowStatus =
      selectedContact && !isInstagramContact(selectedContact) && selectedContact.phone &&
      (selectedContact.active_channel === 'official_whatsapp' || whatsappProviders?.has_official);

    if (shouldLoadWindowStatus) {
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0');
      if (clinicId) {
        console.log('🔄 [WindowStatus] Buscando status da janela 24h para:', selectedContact.phone);
        get24hWindowStatus(clinicId, selectedContact.phone).then(status => {
          if (disposed) return;
          setWindowStatus(status);
          console.log('⏱️ [WindowStatus] Status atualizado:', status);
        }).catch(error => {
          if (disposed) return;
          console.error('❌ [WindowStatus] Erro ao buscar:', error);
          setWindowStatus(null);
        });
      }
    } else {
      setWindowStatus(null);
    }
    return () => { disposed = true; };
  }, [selectedContact, whatsappProviders]);

  // 📱 Carregar providers disponíveis ao selecionar contato
  useEffect(() => {
    let disposed = false;
    if (!selectedContact || isInstagramContact(selectedContact)) {
      setWhatsappProviders(null);
      return;
    }
    if (selectedContact) {
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0');
      if (clinicId) {
        getClinicWhatsAppProviders(clinicId).then(providers => {
          if (disposed) return;
          setWhatsappProviders(providers);
          console.log('📱 [Providers] Providers disponíveis:', providers);
        }).catch(error => {
          if (disposed) return;
          console.error('📱 [Providers] Erro ao carregar providers:', error);
          // Em caso de erro, assumir apenas WAHA
          setWhatsappProviders({ has_waha: true, has_official: false });
        });
      }
    }
    return () => { disposed = true; };
  }, [selectedContact]);

  const contactsRef = useRef<Map<string, Contact>>(new Map());
  const contactsRequestIdRef = useRef(0);
  const shouldSkipInitialFilterReloadRef = useRef(true);

  // Função para arquivar/desarquivar contato
  const handleArchiveToggle = async (contact: Contact, isArchived: boolean) => {
    if (!contact || !conversationKey(contact) || isArchiving) return;

    const clinicId = parseInt(localStorage.getItem('clinic_id') || '0');
    if (!clinicId) {
      console.error('Clinic ID não encontrado');
      return;
    }

    setIsArchiving(true);
    console.log(`🗂️ CHECKPOINT: ${isArchived ? 'Desarquivando' : 'Arquivando'} contato ${contact.phone}`);

    try {
      if (isArchived) {
        await unarchiveContact(conversationKey(contact), clinicId);
      } else {
        await archiveContact(conversationKey(contact), clinicId, 'Arquivado via chat');
      }

      // Recarregar contatos para refletir mudanças
      console.log('✅ CHECKPOINT: Operação concluída, recarregando contatos...');
      await loadContacts(0, CONTACTS_PAGE_SIZE, false);

      // Se o contato arquivado era o selecionado, desselecionar
      if (!isArchived && conversationKey(selectedContact) === conversationKey(contact)) {
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('❌ Erro ao alterar status de arquivo:', error);
      alert('Erro ao ' + (isArchived ? 'desarquivar' : 'arquivar') + ' contato');
    } finally {
      setIsArchiving(false);
    }
  };

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  // Aplicar escopo de contatos definido pela equipe
  useEffect(() => {
    const applyTeamContactScope = () => {
      const isRestricted = hasRestrictedContactScope();
      const restrictedStages = getRestrictedContactStages();
      setIsContactScopeRestricted(isRestricted);

      if (!isRestricted) {
        return;
      }

      setContactFilters((currentFilters) => {
        const alreadyFiltered =
          currentFilters.funnelStages.length === restrictedStages.length &&
          currentFilters.funnelStages.every(stage => restrictedStages.includes(stage)) &&
          currentFilters.activeFlows.length === 0;

        return alreadyFiltered
          ? currentFilters
          : { funnelStages: restrictedStages, activeFlows: [] };
      });
    };

    applyTeamContactScope();
    window.addEventListener('userPermissionsChanged', applyTeamContactScope);

    return () => {
      window.removeEventListener('userPermissionsChanged', applyTeamContactScope);
    };
  }, []);

  // Carrega informações da clínica
  useEffect(() => {
    async function loadClinicInfo() {
      try {
        const info = await getClinicInfo();
        setClinicInfo({
          name: info.name_clinic || 'Clínica',
          logo_url: info.logo_url
        });
      } catch (error) {
        console.error('Erro ao carregar informações da clínica:', error);
      }
    }

    loadClinicInfo();
  }, []);


  // Carrega contatos
  // Carrega contatos com paginação
  const loadContacts = async (offset = 0, limit = CONTACTS_PAGE_SIZE, append = false) => {
    const requestId = ++contactsRequestIdRef.current;
    const startedAt = performance.now();
    const normalizedSearchTerm = normalizeContactSearchTerm(searchTerm);

    if (offset === 0) {
      setIsContactsLoading(true);
      setCurrentOffset(0);
    }
    if (offset > 0) setIsLoadingMoreContacts(true);

    console.log('[Chat] Carregando contatos:', {
      ...contactFilters,
      search: normalizedSearchTerm,
      unread_only: showUnreadOnly,
      offset,
      limit
    });

    try {
      // Preparar parâmetros com lógica correta para arquivados
      const params: any = {
        limit,
        offset,
        search: normalizedSearchTerm,
        unread_only: showUnreadOnly,
        funnel_stages: contactFilters.funnelStages,
        active_flows: contactFilters.activeFlows,
        lightweight: true,
        ...(attendanceEnabled ? { conversation_state: conversationState, assignment: assignmentFilter } : {})
      };

      // Lógica correta para arquivados (igual ao ContactsList)
      if (attendanceEnabled ? conversationState === 'archived' : showArchivedSection) {
        params.archived_only = true;
      } else {
        params.show_archived = false;
      }

      const requestDeliveryScope = contactDeliveryScope();
      const response = await getContacts(params);
      if (requestDeliveryScope !== contactDeliveryScope()) return;

      if (requestId !== contactsRequestIdRef.current) {
        console.log('[Chat] Resposta de contatos ignorada por estar desatualizada:', {
          requestId,
          latestRequestId: contactsRequestIdRef.current,
          offset,
          append
        });
        return;
      }

      const previousContacts = contactsRef.current;
      const previousContactsById = new Map<number, Contact>();
      previousContacts.forEach((contact) => {
        const contactId = Number(contact.id);
        if (Number.isInteger(contactId) && contactId > 0) {
          previousContactsById.set(contactId, contact);
        }
      });
      const cMap = append ? new Map(previousContacts) : new Map<string, Contact>();
      const modeReconciliations = new Map<number, string>();

      // IMPORTANTE: Normalizar os telefones ao criar o Map inicial
      response.contacts.forEach((c) => {
        const normalizedPhone = realContactPhone(c);
        const contactKey = conversationKey(c);
        if (!contactKey) return;
        cMap.forEach((oldContact, oldKey) => {
          if (oldContact.id === c.id && oldKey !== contactKey) cMap.delete(oldKey);
        });
        const contactId = Number(c.id);
        const knownMode = contactModeValueRef.current.get(contactId);
        const existing =
          previousContacts.get(contactKey) ||
          previousContactsById.get(contactId) ||
          (
            knownMode !== undefined
              ? { id: contactId, human_mode: knownMode } as Contact
              : undefined
          );
        const modeDecision = resolvePolledContactMode(existing, c);
        if (
          modeDecision.requiresReconciliation &&
          Number.isInteger(contactId) &&
          contactId > 0
        ) {
          modeReconciliations.set(contactId, normalizedPhone);
        }

        // Processar lastMessage para detectar mídia e tipos especiais
        const processedLastMessage = c.lastMessage || '';

        // Debug: verificar primeiro contato com flow_progress
        if (c.flow_progress && !(window as any)._flowDebugShown) {
          console.log('📱 Primeiro contato com flow_progress:', c);
          (window as any)._flowDebugShown = true;
        }

        const freshMessage = preserveCurrentContactMessage(existing, c);
        const contactToSave = contactDeliveryInbox.current.apply(requestDeliveryScope, contactKey, {
          ...c,
          ...freshMessage,
          ...mergeContactMessageDelivery(existing, freshMessage),
          phone: normalizedPhone,    // Mantém o telefone normalizado
          lastMessage: freshMessage.lastMessage ?? processedLastMessage,
          unreadCount: c.unreadCount ?? 0,
          tags: c.tags || [],         // ✅ Garantir que as tags do backend sejam preservadas
          human_mode: modeDecision.humanMode
        });

        // Debug específico
        if (c.name === "Suporte | Sminy.ai") {
          console.log('🔍 Salvando Suporte | Sminy.ai no Map:', {
            original: c,
            toSave: contactToSave,
            hasFlowProgress: !!contactToSave.flow_progress
          });
        }

        cMap.set(contactKey, contactToSave);
      });

      contactsRef.current = cMap;
      setContacts(cMap);
      modeReconciliations.forEach((phone, contactId) => {
        void reconcileContactMode(contactId, phone);
      });
      setHasMoreContacts(response.has_more);

      // Atualizar offset apenas se for append
      if (append) {
        setCurrentOffset(offset + response.contacts.length);
      } else {
        setCurrentOffset(response.contacts.length);
      }

      // Imprimir as chaves do Map para debug
      console.log('[Chat] Contatos carregados:', {
        total: cMap.size,
        hasMore: response.has_more,
        currentOffset: append ? offset + response.contacts.length : response.contacts.length,
        responseCount: response.contacts.length,
        append,
        durationMs: Math.round(performance.now() - startedAt)
      });
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
    } finally {
      if (requestId === contactsRequestIdRef.current) {
        setIsContactsLoading(false);
        setIsLoadingMoreContacts(false);
      }
    }
  };

  useEffect(() => {
    loadContacts(0, CONTACTS_PAGE_SIZE, false);
  }, []);

  // Lidar com navegação via state (CRM, cadastros e notificações).
  // O payload é copiado para estado/ref local e a rota é consumida na hora,
  // para o composer não ficar preso em selectedPhone residual nem no Map contacts.
  useEffect(() => {
    if (isMobile) return;

    const incoming = readChatNavigationPayload(location.state);
    if (incoming) {
      pendingRouteNavigationRef.current = incoming;
      setPendingChatNavigation(incoming);
      setSelectedContact(null);
      setPendingMessage(null);
      setShowProviderModal(false);
      setShowTemplateSendModal(false);
      setReplyTarget(null);
      setNewMessage('');
      setShowContactSidebar(false);
      setSendError({
        code: 'contact_identity_pending',
        message: 'Identificando o contato selecionado antes de liberar o envio.',
        existingContactId: incoming.selectedContactId > 0 ? incoming.selectedContactId : undefined,
        existingPhone: incoming.normalizedPhone || undefined,
      });
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    const payload = pendingRouteNavigationRef.current;
    if (!payload) return;
    if (isContactsLoading) return;

    let cancelled = false;
    const pendingPhone = payload.normalizedPhone;
    const pendingContactId = payload.selectedContactId;

    const resolveNavigationContact = async () => {
      const currentContacts = contactsRef.current;
      let resolvedContact =
        pickNavigationContact(currentContacts.values(), payload) ||
        (!payload.isInstagram ? currentContacts.get(pendingPhone) : undefined);

      if (!resolvedContact) {
        try {
          let response = await getContacts({
            search: payload.isInstagram ? undefined : payload.rawPhone,
            limit: CONTACTS_PAGE_SIZE,
            lightweight: true,
            show_archived: true,
          });
          if (payload.isInstagram) {
            for (
              let page = 1;
              !cancelled &&
              response.has_more &&
              !response.contacts.some((contact) => contact.id === pendingContactId);
              page += 1
            ) {
              response = await getContacts({
                limit: CONTACTS_PAGE_SIZE,
                offset: page * CONTACTS_PAGE_SIZE,
                lightweight: true,
                show_archived: true,
              });
              if (!response.contacts.length) break;
            }
          }
          resolvedContact = pickNavigationContact(response.contacts, payload);

          if (resolvedContact && !cancelled) {
            const resolvedPhone = realContactPhone(resolvedContact);
            resolvedContact = { ...resolvedContact, phone: resolvedPhone };
            const contactToStore = resolvedContact;
            setContacts((previous) => {
              const next = new Map(previous);
              next.set(conversationKey(contactToStore), contactToStore);
              return next;
            });
          }
        } catch (error) {
          if (!cancelled) {
            setSendError(
              parseApiError(error, 'Não foi possível localizar o contato selecionado.'),
            );
          }
        }
      }

      if (cancelled) return;

      if (resolvedContact) {
        await selectContactFromList({ ...resolvedContact, phone: realContactPhone(resolvedContact) });
      } else {
        setSelectedContact(null);
        setSendError({
          message:
            'Não foi possível identificar um único contato para esta conversa. Confira os cadastros pelo telefone.',
          existingContactId: pendingContactId > 0 ? pendingContactId : undefined,
          existingPhone: pendingPhone,
        });
      }

      if (!cancelled) {
        pendingRouteNavigationRef.current = null;
        setPendingChatNavigation(null);
      }
    };

    resolveNavigationContact();

    return () => {
      cancelled = true;
    };
  }, [
    isContactsLoading,
    isMobile,
    location.key,
    location.pathname,
    location.state,
    navigate,
  ]);

  // Recarregar contatos quando filtros mudarem
  useEffect(() => {
    if (shouldSkipInitialFilterReloadRef.current) {
      shouldSkipInitialFilterReloadRef.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      setCurrentOffset(0);
      setHasMoreContacts(true);
      loadContacts(0, CONTACTS_PAGE_SIZE, false);
    }, CONTACTS_FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, showUnreadOnly, contactFilters.funnelStages, contactFilters.activeFlows, showArchivedSection, attendanceEnabled, conversationState, attendanceRevision, assignmentFilter]);

  // Criar ref para acessar valores atuais dos filtros no polling
  const filtersRef = useRef({ contactFilters, searchTerm, showUnreadOnly, showArchivedSection, attendanceEnabled, conversationState, assignmentFilter });
  useEffect(() => {
    filtersRef.current = { contactFilters, searchTerm, showUnreadOnly, showArchivedSection, attendanceEnabled, conversationState, assignmentFilter };
  }, [contactFilters, searchTerm, showUnreadOnly, showArchivedSection, attendanceEnabled, conversationState, assignmentFilter]);

  // Conectar ao WebSocket unificado quando o componente montar - CORRIGIDO
  useEffect(() => {
    // CORREÇÃO: Não se conectar automaticamente ao modo global
    // Conectar apenas ao WebSocket base sem inscrição global
    unifiedWebSocketManager.connect();

    // Polling otimizado - apenas busca atualizações
    const contactsPolling = setInterval(async () => {
      try {
        // Buscar apenas os primeiros 20 contatos mais recentes para atualizações
        // IMPORTANTE: Usar os filtros atuais através da ref
        const currentFilters = filtersRef.current;
        const attendanceRequestVersion = contactsRequestIdRef.current;

        // Preparar parâmetros com lógica correta para arquivados
        const params: any = {
          limit: 20,
          offset: 0,
          search: currentFilters.searchTerm,
          unread_only: currentFilters.showUnreadOnly,
          funnel_stages: currentFilters.contactFilters.funnelStages,
          active_flows: currentFilters.contactFilters.activeFlows,
          lightweight: true,
          ...(currentFilters.attendanceEnabled ? { conversation_state: currentFilters.conversationState, assignment: currentFilters.assignmentFilter } : {})
        };

        // Lógica correta para arquivados (igual ao ContactsList)
        if (currentFilters.attendanceEnabled ? currentFilters.conversationState === 'archived' : currentFilters.showArchivedSection) {
          params.archived_only = true;
        } else {
          params.show_archived = false;
        }

        const requestDeliveryScope = contactDeliveryScope();
        const response = await getContacts(params);
        if (requestDeliveryScope !== contactDeliveryScope()) return;
        if (attendanceRequestVersion !== contactsRequestIdRef.current) return;

        const cMap = new Map(contactsRef.current);
        const modeReconciliations = new Map<number, string>();
        let hasChanges = false;

        response.contacts.forEach((c) => {
          const normalizedPhone = realContactPhone(c);
          const contactKey = conversationKey(c);
          if (!contactKey) return;
          cMap.forEach((oldContact, oldKey) => {
            if (oldContact.id === c.id && oldKey !== contactKey) { cMap.delete(oldKey); hasChanges = true; }
          });
          const existing = cMap.get(contactKey);
          const knownMode = contactModeValueRef.current.get(Number(c.id));
          const modeBaseline = existing || (
            knownMode !== undefined
              ? { id: Number(c.id), human_mode: knownMode }
              : undefined
          );
          const modeDecision = resolvePolledContactMode(modeBaseline, c);
          const freshMessage = preserveCurrentContactMessage(existing, c);
          const timestampNumber = freshMessage.timestampNumber ?? (c.last_message_at ? new Date(c.last_message_at).getTime() : 0);
          const name = c.name || normalizedPhone;
          const unreadCount = c.unreadCount ?? 0;
          const sourceId = c.source_id ?? null;
          const delivery = contactDeliveryInbox.current.apply(requestDeliveryScope, contactKey, { ...freshMessage, ...mergeContactMessageDelivery(existing, freshMessage) });

          if (
            modeDecision.requiresReconciliation &&
            Number.isInteger(Number(c.id)) &&
            Number(c.id) > 0
          ) {
            modeReconciliations.set(Number(c.id), normalizedPhone);
          }

          if (!existing ||
            existing.timestampNumber !== timestampNumber ||
            existing.unreadCount !== unreadCount ||
            existing.name !== name ||
            (existing.source_id ?? null) !== sourceId ||
            existing.lead_id !== c.lead_id ||
            existing.funnel_stage !== c.funnel_stage ||
            existing.funnel_status?.agendamento_id !== c.funnel_status?.agendamento_id ||
            existing.funnel_status?.comparecimento_id !== c.funnel_status?.comparecimento_id ||
            existing.funnel_status?.venda_id !== c.funnel_status?.venda_id ||
            existing.funnel_status?.no_show_id !== c.funnel_status?.no_show_id ||
            existing.lastMessage !== freshMessage.lastMessage ||
            existing.lastMessageType !== freshMessage.lastMessageType ||
            existing.lastMessageStatus !== delivery.lastMessageStatus ||
            JSON.stringify(existing.lastMessageDeliveryError) !== JSON.stringify(delivery.lastMessageDeliveryError) ||
            existing.lastMessageDeliveryStatusAt !== delivery.lastMessageDeliveryStatusAt ||
            existing.lastMessageFromMe !== freshMessage.lastMessageFromMe ||
            existing.lastMessageProviderId !== freshMessage.lastMessageProviderId ||
            existing.lastMessageSourceChannel !== freshMessage.lastMessageSourceChannel) {
            cMap.set(contactKey, {
              ...existing,
              ...c,
              ...freshMessage,
              lastMessageStatus: delivery.lastMessageStatus,
              lastMessageDeliveryError: delivery.lastMessageDeliveryError,
              lastMessageDeliveryStatusAt: delivery.lastMessageDeliveryStatusAt,
              phone: normalizedPhone,
              photo: c.photo || '',
              name,
              unreadCount,
              timestampNumber,
              lastMessage: freshMessage.lastMessage ?? '',
              lastMessageType: freshMessage.lastMessageType ?? null,
              human_mode: modeDecision.humanMode,
              source_id: c.source_id ?? existing?.source_id,
              lead_id: c.lead_id ?? existing?.lead_id,
              active_channel: c.active_channel || existing?.active_channel || null,
              funnel_stage: c.funnel_stage,
              funnel_status: c.funnel_status,
              flow_progress: c.flow_progress ?? existing?.flow_progress,
              tags: c.tags ?? existing?.tags ?? []
            });
            hasChanges = true;
          }
        });

        if (hasChanges) {
          contactsRef.current = cMap;
          setContacts(cMap);
        }
        modeReconciliations.forEach((phone, contactId) => {
          void reconcileContactMode(contactId, phone);
        });
      } catch (error) {
        console.error('Erro ao atualizar contatos via polling:', error);
      }
    }, 30000); // Aumentar para 30 segundos

    // Limpar quando o componente desmontar
    return () => {
      clearInterval(contactsPolling);
      contactModeReconcileGenerationRef.current.forEach((generation, contactId) => {
        contactModeReconcileGenerationRef.current.set(contactId, generation + 1);
      });
      contactModeReconcileInFlightRef.current.clear();
    };
  }, []);

  // CORREÇÃO: Inscrever-se apenas no contato selecionado
  useEffect(() => {
    if (!selectedContact) return;

    const selectedKey = conversationKey(selectedContact);
    if (!selectedKey) return;
    const subscribedContactId = Number(selectedContact.id);
    // Inscrever no tópico específico do contato selecionado
    unifiedWebSocketManager.subscribe(selectedKey);

    // Registrar handler para mensagens específicas deste contato
    const subscribedDeliveryScope = contactDeliveryScope();
    const unsubscribeContact = unifiedWebSocketManager.onMessage(selectedKey, (data) => {
      if (subscribedDeliveryScope === contactDeliveryScope()) handleWebSocketMessage(data);
    });

    // Limpar quando mudar de contato ou desmontar
    return () => {
      unsubscribeContact();
      unifiedWebSocketManager.unsubscribe(selectedKey);
      if (Number.isInteger(subscribedContactId) && subscribedContactId > 0) {
        invalidateContactModeReconciliation(subscribedContactId);
      }
    };
  }, [selectedContact?.id, selectedContact?.phone, selectedContact?.active_channel]);

  // REMOVIDO: useEffect redundante para mensagens unificadas globais
  // A atualização da lista de contatos agora é feita via polling
  // e mensagens específicas são recebidas via WebSocket do contato selecionado

  // REMOVIDO: useEffect duplicado - já implementado acima

  /**
   * Normaliza um número de telefone para usar como chave no Map.
   * Remove espaços, traços e garante consistência.
   */
  const normalizePhone = (phone: string): string => {
    if (!phone) return '';

    // Remove todos os caracteres não numéricos
    // Isso garante consistência entre '55 1234-5678' e '551234-5678' por exemplo
    let normalized = phone.replace(/\D/g, '');

    // Se quiser manter o formato com país, você pode adicionar lógica aqui
    // Por exemplo, garantir que comece com '55' para Brasil

    return normalized;
  };

  const shouldApplyContactModeVersion = (
    contactId: number,
    auditId?: number | null,
    changedAt?: string | null,
    allowEqual = false,
  ): boolean =>
    registerContactModeVersion(
      contactModeVersionRef.current,
      contactId,
      auditId,
      changedAt,
      { allowEqual },
    );

  const applyAuthoritativeContactMode = ({
    contactId,
    phone,
    humanMode,
    auditId,
    changedAt,
    refreshTimeline = false,
    allowEqualVersion = false,
  }: {
    contactId: number;
    phone?: string;
    humanMode: boolean;
    auditId?: number | null;
    changedAt?: string | null;
    refreshTimeline?: boolean;
    allowEqualVersion?: boolean;
  }): boolean => {
    if (!Number.isInteger(contactId) || contactId <= 0) return false;
    if (
      !shouldApplyContactModeVersion(
        contactId,
        auditId,
        changedAt,
        allowEqualVersion,
      )
    ) {
      return false;
    }

    contactModeValueRef.current.set(contactId, humanMode);
    setModalContacts((previous) =>
      applyContactModeToCollection(previous, contactId, humanMode)
    );
    const normalizedPhone = phone?.startsWith('contact:') ? '' : normalizePhone(phone || '');
    setContacts((previous) => {
      const next = new Map(previous);
      let changed = false;

      next.forEach((contact, key) => {
        const sameContact =
          Number(contact.id) === contactId ||
          (normalizedPhone !== '' &&
            key === normalizedPhone &&
            Number(contact.id) === contactId);
        if (sameContact && contact.human_mode !== humanMode) {
          next.set(key, { ...contact, human_mode: humanMode });
          changed = true;
        }
      });

      const result = changed ? next : previous;
      contactsRef.current = result;
      return result;
    });
    setSelectedContact((previous) =>
      previous && Number(previous.id) === contactId
        ? { ...previous, human_mode: humanMode }
        : previous
    );

    if (refreshTimeline) {
      setTimelineRefreshToken((current) => current + 1);
    }
    return true;
  };

  const invalidateContactModeReconciliation = (contactId: number) => {
    const nextGeneration =
      (contactModeReconcileGenerationRef.current.get(contactId) || 0) + 1;
    contactModeReconcileGenerationRef.current.set(contactId, nextGeneration);
    contactModeReconcileInFlightRef.current.delete(contactId);
  };

  const reconcileContactMode = (
    contactId: number,
    phone?: string,
  ): Promise<void> => {
    if (!Number.isInteger(contactId) || contactId <= 0) {
      return Promise.resolve();
    }

    const existingRequest = contactModeReconcileInFlightRef.current.get(contactId);
    if (existingRequest) return existingRequest;

    const generation =
      (contactModeReconcileGenerationRef.current.get(contactId) || 0) + 1;
    contactModeReconcileGenerationRef.current.set(contactId, generation);

    let request: Promise<void>;
    request = getContactMode(contactId)
      .then((response) => {
        if (
          contactModeReconcileGenerationRef.current.get(contactId) !== generation ||
          Number(response.contact_id) !== contactId
        ) {
          return;
        }

        applyAuthoritativeContactMode({
          contactId,
          phone: response.phone || phone,
          humanMode: response.human_mode === true,
          auditId: response.audit_id ?? response.audit?.id,
          changedAt: response.changed_at ?? response.audit?.created_at,
          refreshTimeline: true,
          allowEqualVersion: true,
        });
      })
      .catch((error) => {
        console.error('[Chat] Falha ao reconciliar modo do contato:', {
          contactId,
          code: (error as any)?.code,
          status: (error as any)?.response?.status,
        });
      })
      .finally(() => {
        if (contactModeReconcileInFlightRef.current.get(contactId) === request) {
          contactModeReconcileInFlightRef.current.delete(contactId);
        }
      });

    contactModeReconcileInFlightRef.current.set(contactId, request);
    return request;
  };

  const handleContactModeConfirmed = (response: ContactModeResponse) => {
    return applyAuthoritativeContactMode({
      contactId: Number(response.contact_id),
      phone: response.phone,
      humanMode: response.human_mode === true,
      auditId: response.audit_id ?? response.audit?.id,
      changedAt: response.changed_at ?? response.audit?.created_at,
      refreshTimeline: response.changed || Boolean(response.audit_id || response.audit?.id),
      allowEqualVersion: true,
    });
  };

  // 2. Agora, vamos modificar o handleWebSocketMessage para usar a função de normalização

  const handleWebSocketMessage = (data: any) => {
    if (data.type === 'connection_established') return;
    if (data.type === 'contact_presence' || data.data?.type === 'contact_presence') return;
    const eventClinic = data.data?.clinic_id ?? data.clinic_id;
    const ownClinic = localStorage.getItem('clinic_id');
    if (eventClinic != null && ownClinic && String(eventClinic) !== ownClinic) return;

    if (data.type === 'message_status') {
      const statusPayload = data.data || data;
      const contactKey = conversationEventKey(data);
      if (!contactKey) return;
      const currentClinic = localStorage.getItem('clinic_id');
      if (statusPayload.clinic_id != null && currentClinic && String(statusPayload.clinic_id) !== currentClinic) return;
      const knownContact = contactsRef.current.get(contactKey);
      if (statusPayload.contact_id != null && knownContact?.id && Number(statusPayload.contact_id) !== knownContact.id) return;
      const scope = contactDeliveryScope();
      contactDeliveryInbox.current.record(scope, contactKey, statusPayload);
      setContacts(previous => {
        const existing = previous.get(contactKey);
        if (!existing) return previous;
        const updated = contactDeliveryInbox.current.apply(scope, contactKey, existing);
        if (updated === existing) return previous;
        const next = new Map(previous);
        next.set(contactKey, updated);
        contactsRef.current = next;
        return next;
      });
      return;
    }

    if (data.type === 'message_reaction') return;

    const modeContactId = data.type === 'contact_mode_changed' ? Number(data.contact_id ?? data.data?.contact_id) : 0;
    const phoneFromMsg = conversationEventKey(data) || (modeContactId > 0
      ? conversationKey(Array.from(contactsRef.current.values()).find(contact => contact.id === modeContactId)) : '');
    if (!phoneFromMsg) return;
    const instagramContactId = conversationContactId(phoneFromMsg);
    const rawPhoneFromMsg = instagramContactId ? '' : (data.phone || data.data?.phone || '');
    const eventPhone = instagramContactId ? '' : normalizePhone(rawPhoneFromMsg);
    if (data.type === 'contact_read') {
      setContacts(previous => {
        const existing = previous.get(phoneFromMsg);
        if (!existing) return previous;
        const next = new Map(previous);
        next.set(phoneFromMsg, { ...existing, unreadCount: 0 });
        contactsRef.current = next;
        return next;
      });
      return;
    }

    // ✅ FIX: Extrair payload correto (source_channel está dentro de data.data)
    const messagePayload = data.data || data;

    // 🔍 Debug: Log active_channel para verificar identificação WhatsApp oficial
    if (messagePayload.active_channel || messagePayload.source_channel) {
      console.log('📱 [WebSocket] Mensagem com canal identificado:', {
        phone: eventPhone,
        active_channel: messagePayload.active_channel,
        source_channel: messagePayload.source_channel,
        provider: messagePayload.provider
      });
    }

    // NOVO: Verificar duplicação usando messageId ou momment + phone
    const messageKey = `${messagePayload.messageId || messagePayload.momment || Date.now()}_${phoneFromMsg}`;
    if (processedWebSocketMessages.current.has(messageKey)) {
      console.log('🚫 Mensagem duplicada ignorada:', messageKey);
      return;
    }
    processedWebSocketMessages.current.add(messageKey);

    // Limpar mensagem do Set após 5 minutos para evitar vazamento de memória
    setTimeout(() => {
      processedWebSocketMessages.current.delete(messageKey);
    }, 5 * 60 * 1000);

    // IMPORTANTE: Se a mensagem é para o contato atualmente selecionado,
    // apenas atualizamos a lista de contatos mas NÃO processamos a mensagem aqui.
    // O useOptimizedMessages vai processar e adicionar ao chat.
    const isForSelectedContact = selectedContact && phoneFromMsg === conversationKey(selectedContact);

    if (isForSelectedContact) {
      console.log('📌 Mensagem para contato selecionado detectada. Deixando useOptimizedMessages processar:', phoneFromMsg);
    } else {
      console.log('🔔 Processando mensagem WebSocket para outro contato:', {
        phoneOriginal: rawPhoneFromMsg,
        phoneNormalizado: phoneFromMsg,
        type: data.type,
        content: typeof data.content === 'string' ? data.content.substring(0, 20) : data.content,
        messageKey: messageKey
      });
    }

    // NOVO: Tratamento inteligente de mensagens próprias
    if (data.fromMe === true) {
      // Se for mensagem manual do celular (fromApi=false), sempre processar
      if (data.fromApi === false) {
        console.log('📱 Mensagem manual do operador recebida:', phoneFromMsg);
        // Continua o processamento normal
      } else {
        // Se fromApi=true, pode ser do chat frontend OU do LLM
        // Verificar se temos localMessageId na mensagem
        const hasLocalMessageId = data.localMessageId && data.localMessageId.startsWith('local_');

        if (hasLocalMessageId) {
          // Mensagem enviada pelo chat frontend - ignorar para evitar duplicação
          console.log('💬 Ignorando notificação de mensagem própria enviada pelo chat:', phoneFromMsg);

          // Apenas atualiza o status/timestamp do contato
          setContacts((prev) => {
            const newMap = new Map(prev);
            const existing = newMap.get(phoneFromMsg);
            if (existing) {
              existing.timestampNumber = Date.now();
              newMap.set(phoneFromMsg, existing);
            }
            return newMap;
          });

          return;
        } else {
          // Mensagem sem localMessageId = provavelmente do LLM ou outra integração
          console.log('🤖 Mensagem de sistema/LLM recebida:', phoneFromMsg);
          // Continua o processamento normal
        }
      }
    }

    // Processar atualizações NPS
    if (data.type === 'nps_update') {
      console.log('🎯 [Chat] Recebeu nps_update via WebSocket:', data);
      // A atualização será processada pelo useOptimizedMessages se for o contato selecionado
      return;
    }

    // A mudança de modo deve respeitar clínica, contato estável e versão do evento.
    if (data.type === 'contact_mode_changed') {
      const currentClinicId = Number(localStorage.getItem('clinic_id'));
      const existing = contactsRef.current.get(phoneFromMsg);
      const selectedMatchesPhone =
        conversationKey(selectedContact) === phoneFromMsg;
      const knownContactId = Number(
        existing?.id ?? (selectedMatchesPhone ? selectedContact?.id : 0) ?? 0
      );
      if (
        !Number.isInteger(knownContactId) ||
        knownContactId <= 0 ||
        (existing && Number(existing.id) !== knownContactId)
      ) {
        return;
      }

      const disposition = classifyContactModeEvent(
        data,
        currentClinicId,
        knownContactId,
      );
      if (disposition === 'ignore') return;

      const eventHumanMode = data.human_mode ?? data.data?.human_mode;
      if (disposition === 'reconcile' || typeof eventHumanMode !== 'boolean') {
        void reconcileContactMode(knownContactId, eventPhone);
        return;
      }

      applyAuthoritativeContactMode({
        contactId: knownContactId,
        phone: eventPhone,
        humanMode: eventHumanMode,
        auditId: data.audit_id ?? data.data?.audit_id,
        changedAt: data.changed_at ?? data.data?.changed_at,
        refreshTimeline: true,
      });
      return;
    }

    // Timestamp atual para a mensagem
    const providedTime = messagePayload.timestamp ?? messagePayload.momment;
    const parsedTime = typeof providedTime === 'number'
      ? (providedTime < 10000000000 ? providedTime * 1000 : providedTime)
      : typeof providedTime === 'string' ? Date.parse(providedTime) : NaN;
    const currentTimestamp = Number.isFinite(parsedTime) && parsedTime > 0 ? parsedTime : Date.now();

    // Keep the provider type and content together; presentation is shared by both lists.
    const detectedType = messagePayload.type || messagePayload.message_type || null;
    const lastMessage = typeof messagePayload.content === 'string'
      ? messagePayload.content
      : messagePayload.content ? JSON.stringify(messagePayload.content) : '';

    // Acesso direto ao Map atual através do ref
    const currentContacts = contactsRef.current;
    console.log('🔍 Estado atual de contatos:', {
      totalContacts: currentContacts.size,
      hasContact: currentContacts.has(phoneFromMsg),
      detectedType: detectedType
    });

    // IMPORTANTE: Apenas incrementar contador de não lidas se a mensagem não for do usuário
    const isFromUser = messagePayload.fromMe === true;
    const isFromLLM = messagePayload.senderName === 'LLM';
    const messageProviderId = messagePayload.providerMessageId || messagePayload.messageId || null;
    const messageStatus = messagePayload.status || (instagramContactId ? instagramDeliveryStatus(messagePayload.ack) : undefined) || (isFromUser ? 'sent' : undefined);
    const messageSourceChannel = instagramContactId ? 'instagram' : (messagePayload.source_channel || messagePayload.active_channel || null);

    // IMPORTANTE: Verificar se o contato está selecionado atualmente
    const isContactSelected = conversationKey(selectedContact) === phoneFromMsg;
    if (instagramContactId && isContactSelected && !isFromUser) {
      void instagramConversation.reload();
      void markInstagramRead(instagramContactId).catch(() => {
        console.error('[Chat] Falha ao registrar leitura do Instagram');
      });
    }

    // Atualiza o Map de contatos
    setContacts((prev) => {
      // Clone o Map para não modificar o original
      const newMap = new Map(prev);
      const existing = newMap.get(phoneFromMsg);

      if (!existing) {
        console.log('➕ Criando novo contato:', phoneFromMsg);

        // Se não for do usuário, não for do LLM e não estiver selecionado => unread = 1
        const unreadCount = (!isFromUser && !isFromLLM && !isContactSelected) ? 1 : 0;

        const persistedId = persistedWhatsAppContactId(
          instagramContactId,
          messagePayload.contact_id,
          data.contact_id,
        );
        const newContact: Contact = {
          id: persistedId,
          phone: eventPhone,
          name: resolveTemporaryContactName(messagePayload, eventPhone || 'Instagram'),
          photo: messagePayload.photo || '',
          lastMessage: lastMessage,
          lastMessageType: detectedType,
          timestamp: new Date(currentTimestamp).toLocaleTimeString(),
          timestampNumber: currentTimestamp,
          unreadCount,
          human_mode: false,
          active_channel: messageSourceChannel,  // ✅ Identificar WhatsApp oficial
          lastMessageStatus: messageStatus,
          lastMessageFromMe: isFromUser,
          lastMessageProviderId: messageProviderId,
          lastMessageSourceChannel: messageSourceChannel,
          lastMessageDeliveryError: messagePayload.deliveryError,
          lastMessageDeliveryStatusAt: messagePayload.deliveryStatusAt,
        };
        newMap.set(phoneFromMsg, contactDeliveryInbox.current.apply(contactDeliveryScope(), phoneFromMsg, newContact));
      } else {
        console.log('🔄 Atualizando contato existente:', phoneFromMsg);

        // Só incrementa se:
        // 1) mensagem não é do usuário (isFromUser=false)
        // 2) não é do LLM
        // 3) contato não está selecionado
        const shouldIncrementUnread = (!isFromUser && !isFromLLM && !isContactSelected);

        const persistedId = persistedWhatsAppContactId(
          instagramContactId,
          messagePayload.contact_id,
          data.contact_id,
        );
        const existingId = Number(existing.id || 0);
        const updatedContact: Contact = {
          ...existing,
          id: existingId > 0 ? existing.id : (persistedId || existing.id),
          phone: eventPhone, // garantir telefone correto
          lastMessage: lastMessage,
          lastMessageType: detectedType,
          timestamp: new Date(currentTimestamp).toLocaleTimeString(),
          timestampNumber: currentTimestamp,
          unreadCount: shouldIncrementUnread
            ? (existing.unreadCount || 0) + 1
            : (existing.unreadCount || 0),
          active_channel: messageSourceChannel || existing.active_channel || null,  // ✅ Identificar WhatsApp oficial
          lastMessageStatus: messageStatus,
          lastMessageFromMe: isFromUser,
          lastMessageProviderId: messageProviderId,
          lastMessageSourceChannel: messageSourceChannel || existing.lastMessageSourceChannel || null,
          lastMessageDeliveryError: messagePayload.deliveryError,
          lastMessageDeliveryStatusAt: messagePayload.deliveryStatusAt,
        };
        console.log('✅ Atualizando contato com lastMessage:', {
          phone: eventPhone,
          lastMessage: lastMessage,
          previousMessage: existing.lastMessage,
          type: detectedType
        });

        // Se for do LLM, zera contador (caso queira sobrescrever mesmo se já tinha valor)
        if (isFromLLM) {
          updatedContact.unreadCount = 0;
        }

        const freshMessage = preserveCurrentContactMessage(existing, updatedContact);
        newMap.set(phoneFromMsg, contactDeliveryInbox.current.apply(contactDeliveryScope(), phoneFromMsg, {
          ...freshMessage, ...mergeContactMessageDelivery(existing, freshMessage),
        }));
      }

      console.log('✅ Contato atualizado no setter. Total contatos:', newMap.size);
      contactsRef.current = newMap;
      return newMap;
    });

    // Verificação após a atualização do estado
    setTimeout(() => {
      // Use contactsRef para acessar o valor mais recente de contacts
      const currentContact = contactsRef.current.get(phoneFromMsg);

      console.log('Verificação pós-atualização:', {
        phone: eventPhone,
        encontrado: !!currentContact,
        timestampNumber: currentContact?.timestampNumber,
        lastMessage: currentContact?.lastMessage,
        detectedType: detectedType,
        unreadCount: currentContact?.unreadCount
      });

      // Se o contato não for encontrado, vamos procurar em todas as chaves
      if (!currentContact && !instagramContactId) {
        console.warn('Contato não encontrado após atualização. Verificando todas as chaves...');
        const allKeys = Array.from(contactsRef.current.keys());
        console.log('Todas as chaves:', allKeys);

        // Tenta encontrar chaves similares
        const similarKeys = allKeys.filter(key =>
          key.includes(phoneFromMsg.substring(phoneFromMsg.length - 8)) ||
          phoneFromMsg.includes(key.substring(key.length - 8))
        );

        if (similarKeys.length > 0) {
          console.log('Encontradas chaves similares:', similarKeys);
          similarKeys.forEach(key => {
            console.log(`Contato com chave similar ${key}:`, contactsRef.current.get(key));
          });
        }
      }
    }, 100);

    // IMPORTANTE: Se a mensagem é para o contato selecionado, 
    // paramos aqui para evitar processamento duplo
    if (isForSelectedContact) {
      console.log('⏹️ Parando processamento aqui. useOptimizedMessages cuidará desta mensagem.');
      return;
    }
  };

  // Função auxiliar para depuração que pode ser chamada após receber uma mensagem
  useEffect(() => {
    const subscribedDeliveryScope = contactDeliveryScope();
    return unifiedWebSocketManager.onMessage('*', (event: any) => {
      if (subscribedDeliveryScope !== contactDeliveryScope()) return;
      if (conversationContactId(conversationEventKey(event)) !== null || ['message_status', 'new_message'].includes(event.type)) handleWebSocketMessage(event);
    });
  }, [selectedContact?.id, selectedContact?.phone, selectedContact?.active_channel]);

  const debugContactState = (phoneNumber: string) => {
    const contact = contacts.get(phoneNumber);
    console.log('DEBUG CONTATO:', {
      phone: phoneNumber,
      encontrado: !!contact,
      dados: contact ? {
        name: contact.name,
        timestampNumber: contact.timestampNumber,
        lastMessage: contact.lastMessage
      } : 'não encontrado'
    });

    // Verifica se este contato aparece na lista filtrada
    const naListaFiltrada = filteredAndSortedContacts.some(c => c.phone === phoneNumber);
    console.log(`Contato ${phoneNumber} está na lista filtrada? ${naListaFiltrada}`);

    if (!naListaFiltrada && contact && contact.timestampNumber) {
      console.warn('ALERTA: Contato tem timestampNumber mas não está na lista filtrada!');
    }
  };

  // Função para enviar mensagem de texto
  const handleSendMessage = async () => {
    if (isInstagramContact(selectedContact)) {
      if (!selectedContact || !newMessage.trim() || instagramConversation.loading || instagramConversation.sending) return;
      const key = conversationKey(selectedContact);
      const content = newMessage;
      try {
        // The hook owns provider capability checks and the pending idempotency key.
        const result = await instagramConversation.send(content, 'text');
        if (result.status !== 'sent') {
          if (selectedConversationRef.current === key) setSendError({
            code: result.error_code || 'instagram_send_unconfirmed',
            message: result.status === 'failed' ? 'Não foi possível enviar a mensagem pelo Instagram.'
              : 'O envio ainda não foi confirmado. O texto foi preservado; não houve nova tentativa automática.',
          });
          return;
        }
        setContacts(previous => {
          const current = previous.get(key);
          if (!current) return previous;
          const next = new Map(previous);
          next.set(key, { ...current, phone: '', lastMessage: content, lastMessageType: 'text',
            lastMessageFromMe: true, lastMessageStatus: 'sent', lastMessageSourceChannel: 'instagram',
            lastMessageProviderId: result.provider_message_id || null, timestampNumber: Date.now() });
          contactsRef.current = next;
          return next;
        });
        if (selectedConversationRef.current === key) {
          sendMessage(content, 'text', String(result.message_id), 'instagram', {
            status: 'sent', providerMessageId: result.provider_message_id,
          });
          setNewMessage(current => current === content ? '' : current);
          setReplyTarget(null);
          setSendError(null);
        }
      } catch (error) {
        if (selectedConversationRef.current === key) setSendError({
          code: 'instagram_send_unconfirmed',
          message: error instanceof Error ? error.message : 'Não foi possível confirmar o envio pelo Instagram.',
        });
      }
      return;
    }
    if (!newMessage.trim() || !selectedContact) return;
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    const localMessageId = `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    let optimisticMessageCreated = false;
    try {
      setSendError(null);
      const currentReplyTarget = replyTarget;
      const replyToMessageId = currentReplyTarget?.providerMessageId || undefined;
      const replyMetadata = buildReplyMetadata(currentReplyTarget);

      // ✅ NOVO: Verificar se tem múltiplos providers
      if (whatsappProviders?.has_waha && whatsappProviders?.has_official) {
        console.log('📱 [Providers] Clínica tem ambos providers - mostrando modal');
        // Tem ambos - perguntar qual usar
        setPendingMessage({
          type: 'text',
          content: newMessage,
          localMessageId,
          replyTarget: currentReplyTarget
        });
        setShowProviderModal(true);
        setNewMessage(''); // Limpar campo
        setReplyTarget(null);
        return; // Aguarda seleção no modal
      }

      // ✅ NOVO: Se tem apenas API Oficial, usar ela
      if (!whatsappProviders?.has_waha && whatsappProviders?.has_official) {
        console.log('📱 [Providers] Usando apenas API Oficial');
        await sendViaOfficial('text', newMessage, localMessageId);
        setNewMessage('');
        setReplyTarget(null);
        return;
      }

      // ✅ PADRÃO: Usar WAHA (mantém funcionamento atual intacto)
      console.log('📱 [Providers] Usando WAHA (padrão)');

      // Enviar mensagem usando o hook (também atualizará o estado local)
      sendMessage(newMessage, 'text', localMessageId, undefined, replyMetadata ? { replyTo: replyMetadata } : undefined);
      optimisticMessageCreated = true;

      // Enviar para o WhatsApp com o ID local
      const sendResult = await sendWhatsAppText({
        phone: selectedContact.phone,
        message: newMessage,
        localMessageId: localMessageId,
        replyToMessageId,
        contact_id: contactId,
      });
      const providerMessageId = extractProviderMessageId(sendResult);
      updateMessageTracking(localMessageId, providerMessageId, 'sent', sendResult?.sent_content);

      // Limpar campo de texto
      setNewMessage('');
      setReplyTarget(null);

      // Atualizar contato na lista
      setContacts((prev) => {
        const newContacts = new Map(prev);
        const c = newContacts.get(conversationKey(selectedContact));
        if (c) {
          c.lastMessage = sendResult?.sent_content ?? newMessage;
          c.timestamp = new Date().toLocaleTimeString();
          c.timestampNumber = Date.now();
          applySentLastMessageMetadata(c, { providerMessageId, sourceChannel: 'waha' });
          newContacts.set(conversationKey(selectedContact), c);
        }
        return newContacts;
      });
    } catch (error) {
      console.error('[handleSendMessage] -> Erro ao enviar texto:', error);
      if (optimisticMessageCreated) {
        updateMessageTracking(localMessageId, null, 'failed');
      }
      setSendError(parseApiError(error, 'Não foi possível enviar a mensagem.'));
    }
  };

  // ✅ NOVO: Função auxiliar para enviar via API Oficial
  const sendViaOfficial = async (
    type: 'text' | 'audio' | 'image' | 'video',
    content: any,
    localMessageId: string,
    metadata?: { duration?: number }  // ✅ Metadados opcionais (ex: duração do áudio)
  ) => {
    if (isInstagramContact(selectedContact)) return;
    if (!selectedContact) return;
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    try {
      setSendError(null);
      console.log(`📱 [Official] Enviando ${type} via API Oficial`);

      // ✅ Para áudio, criar localMessageContent com URL.createObjectURL
      let displayContent = content;
      if (type === 'audio' && content instanceof Blob) {
        displayContent = {
          url: URL.createObjectURL(content),
          mimeType: content.type,
          duration: metadata?.duration || (pendingMessage as any)?.duration || 0
        };
      }

      // Enviar mensagem local para UI imediatamente com source_channel
      sendMessage(displayContent, type, localMessageId, 'official_whatsapp');

      if (type === 'text') {
        // Texto
        const sendResult = await sendOfficialWhatsAppText({
          phone: selectedContact.phone,
          message: content,
          contact_id: contactId,
        });
        updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
        // Atualizar contato
        setContacts((prev) => {
          const newContacts = new Map(prev);
          const c = newContacts.get(conversationKey(selectedContact));
          if (c) {
            c.lastMessage = sendResult?.sent_content ?? content;
            c.timestamp = new Date().toLocaleTimeString();
            c.timestampNumber = Date.now();
            applySentLastMessageMetadata(c, { providerMessageId: extractProviderMessageId(sendResult), sourceChannel: 'official_whatsapp' });
            newContacts.set(conversationKey(selectedContact), c);
          }
          return newContacts;
        });
      } else if (type === 'audio') {
        // Áudio - precisa fazer upload primeiro
        const audioUrl = await uploadMediaForOfficial(content, 'audio.ogg');
        const sendResult = await sendOfficialWhatsAppAudio({
          phone: selectedContact.phone,
          audio_url: audioUrl,
          contact_id: contactId,
        });
        updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent');
        setContacts((prev) => {
          const newContacts = new Map(prev);
          const c = newContacts.get(conversationKey(selectedContact));
          if (c) {
            c.lastMessage = '🔊 Áudio';
            c.timestamp = new Date().toLocaleTimeString();
            c.timestampNumber = Date.now();
            applySentLastMessageMetadata(c, { providerMessageId: extractProviderMessageId(sendResult), messageType: 'audio', sourceChannel: 'official_whatsapp' });
            newContacts.set(conversationKey(selectedContact), c);
          }
          return newContacts;
        });
      } else if (type === 'image') {
        // Imagem - converter base64 para Blob e fazer upload
        const blob = await fetch(content.url).then(r => r.blob());
        const imageUrl = await uploadMediaForOfficial(blob, 'image.jpg');
        const sendResult = await sendOfficialWhatsAppImage({
          phone: selectedContact.phone,
          image_url: imageUrl,
          caption: content.caption,
          contact_id: contactId,
        });
        updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
        setContacts((prev) => {
          const newContacts = new Map(prev);
          const c = newContacts.get(conversationKey(selectedContact));
          if (c) {
            c.lastMessage = sendResult?.sent_content ?? JSON.stringify({ url: imageUrl, caption: content.caption });
            c.timestamp = new Date().toLocaleTimeString();
            c.timestampNumber = Date.now();
            applySentLastMessageMetadata(c, { providerMessageId: extractProviderMessageId(sendResult), messageType: 'image', sourceChannel: 'official_whatsapp' });
            newContacts.set(conversationKey(selectedContact), c);
          }
          return newContacts;
        });
      } else if (type === 'video') {
        // Vídeo - converter base64 para Blob e fazer upload
        const blob = await fetch(content.url).then(r => r.blob());
        const videoUrl = await uploadMediaForOfficial(blob, 'video.mp4');
        const sendResult = await sendOfficialWhatsAppVideo({
          phone: selectedContact.phone,
          video_url: videoUrl,
          caption: content.caption,
          contact_id: contactId,
        });
        updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
        setContacts((prev) => {
          const newContacts = new Map(prev);
          const c = newContacts.get(conversationKey(selectedContact));
          if (c) {
            c.lastMessage = sendResult?.sent_content ?? JSON.stringify({ url: videoUrl, caption: content.caption });
            c.timestamp = new Date().toLocaleTimeString();
            c.timestampNumber = Date.now();
            applySentLastMessageMetadata(c, { providerMessageId: extractProviderMessageId(sendResult), messageType: 'video', sourceChannel: 'official_whatsapp' });
            newContacts.set(conversationKey(selectedContact), c);
          }
          return newContacts;
        });
      }

      console.log(`📱 [Official] ✅ ${type} enviado com sucesso`);
    } catch (error) {
      console.error(`📱 [Official] ❌ Erro ao enviar ${type}:`, error);
      updateMessageTracking(localMessageId, null, 'failed');
      throw error;
    }
  };

  // ✅ NOVO: Callback quando usuário seleciona provider no modal
  const handleProviderSelected = async (provider: 'waha' | 'official') => {
    if (isInstagramContact(selectedContact)) return;
    console.log(`📱 [Providers] Usuário selecionou: ${provider}`);
    setShowProviderModal(false);

    if (!pendingMessage || !selectedContact) return;
    const contactId = requireSelectedContactId();
    if (!contactId) {
      setPendingMessage(null);
      return;
    }

    const { type, content, localMessageId, replyTarget: pendingReplyTarget } = pendingMessage;
    setPendingMessage(null);

    let optimisticMessageCreated = false;
    try {
      setSendError(null);
      // ✅ NOVO: Detectar se é NPS
      if (content === 'nps') {
        console.log('📊 [NPS] Enviando NPS via WAHA');
        await executeSendNPS();
        return;
      }

      if (provider === 'official') {
        // Enviar via API Oficial
        await sendViaOfficial(type, content, localMessageId);
      } else {
        // Enviar via WAHA (código original)
        if (type === 'text') {
          const replyToMessageId = pendingReplyTarget?.providerMessageId || undefined;
          const replyMetadata = buildReplyMetadata(pendingReplyTarget || null);
          sendMessage(content, 'text', localMessageId, undefined, replyMetadata ? { replyTo: replyMetadata } : undefined);
          optimisticMessageCreated = true;
          const sendResult = await sendWhatsAppText({
            phone: selectedContact.phone,
            message: content,
            localMessageId: localMessageId,
            replyToMessageId,
            contact_id: contactId,
          });
          const providerMessageId = extractProviderMessageId(sendResult);
          updateMessageTracking(localMessageId, providerMessageId, 'sent', sendResult?.sent_content);
          setReplyTarget(null);
          setContacts((prev) => {
            const newContacts = new Map(prev);
            const c = newContacts.get(conversationKey(selectedContact));
            if (c) {
              c.lastMessage = sendResult?.sent_content ?? content;
              c.timestamp = new Date().toLocaleTimeString();
              c.timestampNumber = Date.now();
              applySentLastMessageMetadata(c, { providerMessageId, sourceChannel: 'waha' });
              newContacts.set(conversationKey(selectedContact), c);
            }
            return newContacts;
          });
        } else if (type === 'audio') {
          // ✅ Criar localMessageContent para mostrar áudio localmente
          const localMessageContent = {
            url: URL.createObjectURL(content),  // content é o Blob
            mimeType: content.type,
            duration: (pendingMessage as any).duration || 0  // Recuperar duração armazenada
          };

          // ✅ Mostrar mensagem localmente PRIMEIRO
          sendMessage(localMessageContent, 'audio', localMessageId);
          optimisticMessageCreated = true;

          // ✅ Depois enviar para backend via WAHA
          await sendWhatsAppAudioDirect({
            phone: selectedContact.phone,
            audioBlob: content,
            convert: true,
            localMessageId,
            contact_id: contactId,
          });

          // ✅ Atualizar lista de contatos
          setContacts((prev) => {
            const newContacts = new Map(prev);
            const c = newContacts.get(conversationKey(selectedContact));
            if (c) {
              c.lastMessage = '🔊 Áudio';
              c.timestamp = new Date().toLocaleTimeString();
              c.timestampNumber = Date.now();
              applySentLastMessageMetadata(c, { messageType: 'audio', sourceChannel: 'waha' });
              newContacts.set(conversationKey(selectedContact), c);
            }
            return newContacts;
          });
        } else if (type === 'image' || type === 'video') {
          // ✅ Mostrar mensagem localmente PRIMEIRO
          sendMessage(content, type, localMessageId);
          optimisticMessageCreated = true;

          // ✅ Depois enviar para backend via WAHA
          if (type === 'image') {
            const sendResult = await sendWhatsAppImage({
              phone: selectedContact.phone,
              image: content.url,
              caption: content.caption,
              localMessageId: localMessageId,
              contact_id: contactId,
            });
            updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
          } else {
            const sendResult = await sendWhatsAppVideo({
              phone: selectedContact.phone,
              video: content.url,
              caption: content.caption,
              localMessageId: localMessageId,
              contact_id: contactId,
            });
            updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
          }

          // ✅ Atualizar lista de contatos
          setContacts((prev) => {
            const newContacts = new Map(prev);
            const c = newContacts.get(conversationKey(selectedContact));
            if (c) {
              c.lastMessage = JSON.stringify({ caption: content.caption });
              c.timestamp = new Date().toLocaleTimeString();
              c.timestampNumber = Date.now();
              applySentLastMessageMetadata(c, { messageType: type, sourceChannel: 'waha' });
              newContacts.set(conversationKey(selectedContact), c);
            }
            return newContacts;
          });
        }
      }
    } catch (error) {
      console.error(`[handleProviderSelected] Erro ao enviar via ${provider}:`, error);
      if (optimisticMessageCreated) {
        updateMessageTracking(localMessageId, null, 'failed');
      }
      setSendError(parseApiError(error, 'Não foi possível enviar a mensagem.'));
    }
  };

  const handleReplyToMessage = (message: OptimizedMessage) => {
    if (isInstagramContact(selectedContact)) return;
    if (!message.providerMessageId) {
      console.warn('[Reply] Mensagem sem providerMessageId; reply WAHA indisponível', message.id);
      return;
    }
    setReplyTarget(message);
  };

  const handleReactToMessage = async (message: OptimizedMessage, emoji: string) => {
    if (isInstagramContact(selectedContact)) return;
    if (!selectedContact || !message.providerMessageId) return;
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    try {
      setSendError(null);
      await reactToWhatsAppMessage({
        phone: selectedContact.phone,
        messageId: message.providerMessageId,
        reaction: emoji,
        contact_id: contactId,
      });
    } catch (error) {
      console.error('[Reaction] Erro ao enviar reação:', error);
      setSendError(parseApiError(error, 'Não foi possível enviar a reação.'));
    }
  };

  const handleInstagramFile = async (file: File): Promise<boolean> => {
    if (!isInstagramContact(selectedContact) || !selectedContact) return false;
    if (newMessage.trim()) {
      setSendError({ code: 'instagram_caption_unsupported',
        message: 'O Instagram não aceita legenda neste envio. Envie o texto separadamente antes de enviar o arquivo.' });
      return false;
    }
    if (instagramConversation.loading || instagramConversation.sending) return false;
    const key = conversationKey(selectedContact);
    try {
      const result = await instagramConversation.sendFile(file);
      if (result.status !== 'sent') {
        if (selectedConversationRef.current === key) setSendError({
          code: result.error_code || 'instagram_send_unconfirmed',
          message: result.status === 'failed' ? 'Não foi possível enviar o arquivo pelo Instagram.'
            : 'O envio do arquivo ainda não foi confirmado. Não houve nova tentativa automática.',
        });
        return false;
      }
      setContacts(previous => {
        const current = previous.get(key);
        if (!current) return previous;
        const next = new Map(previous);
        next.set(key, { ...current, phone: '', lastMessage: result.content_url || '',
          lastMessageType: result.message_type, lastMessageFromMe: true, lastMessageStatus: 'sent',
          lastMessageSourceChannel: 'instagram', lastMessageProviderId: result.provider_message_id || null,
          timestampNumber: Date.now() });
        contactsRef.current = next;
        return next;
      });
      if (selectedConversationRef.current === key) {
        sendMessage({ url: result.content_url }, result.message_type, String(result.message_id), 'instagram', {
          status: 'sent', providerMessageId: result.provider_message_id,
        });
        setSendError(null);
      }
      return true;
    } catch (error) {
      if (selectedConversationRef.current === key) setSendError({ code: 'instagram_send_unconfirmed',
        message: error instanceof Error ? error.message : 'Não foi possível confirmar o envio do arquivo.' });
      return false;
    }
  };

  // Função para enviar mensagem de áudio
  const handleAudioRecorded = async (audioBlob: Blob, recordedDuration: number) => {
    if (isInstagramContact(selectedContact)) {
      // Keep the same File for this recording; the shared hook owns idempotency.
      let file = instagramAudioFilesRef.current.get(audioBlob);
      if (!file) {
        const extension = audioBlob.type.includes('ogg') ? 'ogg' : audioBlob.type.includes('mp4') ? 'm4a' : 'webm';
        file = new File([audioBlob], `audio.${extension}`, { type: audioBlob.type });
        instagramAudioFilesRef.current.set(audioBlob, file);
      }
      await handleInstagramFile(file);
      return;
    }
    if (!selectedContact) return;
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    const localMessageId = `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    let optimisticMessageCreated = false;
    try {
      setSendError(null);
      console.log("[AUDIO] 🚀 Áudio gravado");
      console.log("[AUDIO] Áudio gravado com", recordedDuration, "segundos");
      console.log("[AUDIO] Tamanho do blob:", audioBlob.size, "bytes");
      console.log("[AUDIO] Tipo MIME:", audioBlob.type);

      console.log("[AUDIO] ID da mensagem local:", localMessageId);

      // ✅ NOVO: Verificar se tem múltiplos providers
      if (whatsappProviders?.has_waha && whatsappProviders?.has_official) {
        console.log('📱 [Providers] Clínica tem ambos providers - mostrando modal para áudio');
        setPendingMessage({
          type: 'audio',
          content: audioBlob,  // Armazenar o Blob original
          localMessageId,
          duration: recordedDuration  // ✅ Armazenar duração para usar depois
        });
        // ✅ NÃO mostrar mensagem ainda - aguardar seleção do provider no modal
        setShowProviderModal(true);
        return;
      }

      // ✅ NOVO: Se tem apenas API Oficial, usar ela
      if (!whatsappProviders?.has_waha && whatsappProviders?.has_official) {
        console.log('📱 [Providers] Usando apenas API Oficial para áudio');
        await sendViaOfficial('audio', audioBlob, localMessageId, { duration: recordedDuration });
        return;
      }

      // ✅ PADRÃO: Fluxo WAHA otimizado (mantém funcionamento atual intacto)
      console.log("[AUDIO] Usando fluxo WAHA otimizado (FormData direto)");

      // Fluxo otimizado WAHA: não converter para base64
      const localMessageContent = {
        url: URL.createObjectURL(audioBlob),  // URL temporária para UI
        mimeType: audioBlob.type,
        duration: recordedDuration
      };

      console.log("[AUDIO] Conteúdo da mensagem local WAHA:", {
        mimeType: localMessageContent.mimeType,
        duration: localMessageContent.duration,
        urlType: 'blob'
      });

      // Enviar mensagem local para UI imediatamente
      sendMessage(localMessageContent, 'audio', localMessageId);
      optimisticMessageCreated = true;

      // Enviar via WAHA direto (FormData, sem base64) - sem verificação desnecessária
      try {
        console.log("[AUDIO] Enviando via WAHA Direct (FormData)...");
        const result = await sendWhatsAppAudioDirect({
          phone: selectedContact.phone,
          audioBlob: audioBlob,
          convert: true,
          localMessageId,
          contact_id: contactId,
        });
        console.log("[AUDIO] ✅ Áudio enviado via WAHA Direct:", result);

        // Atualizar a listagem de contatos
        setContacts((prev) => {
          const newContacts = new Map(prev);
          const c = newContacts.get(conversationKey(selectedContact));
          if (c) {
            c.lastMessage = '🔊 Áudio';
            c.timestamp = new Date().toLocaleTimeString();
            c.timestampNumber = Date.now();
            applySentLastMessageMetadata(c, { messageType: 'audio', sourceChannel: 'waha' });
            newContacts.set(conversationKey(selectedContact), c);
          }
          return newContacts;
        });
      } catch (wahaError) {
        console.error("[AUDIO] ❌ Erro ao enviar via WAHA Direct:", wahaError);
        const parsedWahaError = parseApiError(wahaError, 'Não foi possível enviar o áudio.');
        const isIdentityError = [
          'invalid_phone',
          'whatsapp_number_not_found',
          'whatsapp_validation_unavailable',
          'whatsapp_identity_ambiguous',
          'whatsapp_identity_conflict',
        ].includes(parsedWahaError.code || '');
        if (isIdentityError || parsedWahaError.existingContactId) {
          updateMessageTracking(localMessageId, null, 'failed');
          setSendError(parsedWahaError);
          return;
        }
        // Fallback para fluxo tradicional
        console.log("[AUDIO] Fazendo fallback para fluxo tradicional...");

        // Converter Blob para base64 para fallback
        const reader = new FileReader();
        reader.onload = async (e) => {
          if (!e.target?.result) {
            updateMessageTracking(localMessageId, null, 'failed');
            setSendError({ message: 'Não foi possível processar o áudio para envio.' });
            return;
          }
          const base64Content = e.target.result.toString();
          console.log("[AUDIO] Base64 gerado para fallback, tamanho:", base64Content.length);

          const fallbackContent = {
            url: base64Content,
            mimeType: audioBlob.type,
            duration: recordedDuration
          };

          try {
            const payload: SendAudioParams = {
              phone: selectedContact.phone,
              audio: base64Content,
              localMessageId: localMessageId,
              contact_id: contactId,
            };
            await sendWhatsAppAudio(payload);

            // Atualizar a listagem de contatos
            setContacts((prev) => {
              const newContacts = new Map(prev);
              const c = newContacts.get(conversationKey(selectedContact));
              if (c) {
                c.lastMessage = '🔊 Áudio';
                c.timestamp = new Date().toLocaleTimeString();
                c.timestampNumber = Date.now();
                applySentLastMessageMetadata(c, { messageType: 'audio', sourceChannel: 'waha' });
                newContacts.set(conversationKey(selectedContact), c);
              }
              return newContacts;
            });
          } catch (fallbackErr) {
            console.error('[AUDIO] Erro no fallback:', fallbackErr);
            updateMessageTracking(localMessageId, null, 'failed');
            setSendError(parseApiError(fallbackErr, 'Não foi possível enviar o áudio.'));
          }
        };
        reader.onerror = () => {
          updateMessageTracking(localMessageId, null, 'failed');
          setSendError({ message: 'Não foi possível processar o áudio para envio.' });
        };
        reader.readAsDataURL(audioBlob);
      }
    } catch (err) {
      console.error('Erro ao processar áudio gravado:', err);
      if (optimisticMessageCreated) {
        updateMessageTracking(localMessageId, null, 'failed');
      }
      setSendError(parseApiError(err, 'Não foi possível enviar o áudio.'));
    }
  };

  // Função para enviar NPS
  const handleSendNPS = async () => {
    if (isInstagramContact(selectedContact)) return;
    console.log('handleSendNPS chamado!', selectedContact?.phone);
    if (!selectedContact) {
      console.log('Nenhum contato selecionado');
      return;
    }
    if (!requireSelectedContactId()) return;

    // ✅ NOVO: Verificar se tem múltiplos providers antes de enviar NPS
    if (whatsappProviders?.has_waha && whatsappProviders?.has_official) {
      console.log('📱 [NPS] Clínica tem ambos providers - mostrando modal (Official desabilitado para NPS)');
      // Armazenar que é NPS pendente
      setPendingMessage({
        type: 'text', // Tipo genérico, mas será processado como NPS
        content: 'nps', // Marcador especial para NPS
        localMessageId: 'nps'
      });
      setShowProviderModal(true);
      return; // Aguarda seleção no modal (só WAHA será permitido)
    }

    // ✅ Se não tem múltiplos providers OU só tem WAHA, enviar direto
    await executeSendNPS();
  };

  // ✅ NOVO: Função auxiliar para executar envio de NPS
  const executeSendNPS = async () => {
    if (!selectedContact) return;
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    try {
      setSendError(null);
      const question = 'Em uma escala de 1 a 5, como você avalia nosso atendimento?';

      // Enviar direto para o backend (que irá salvar na tabela messages)
      const response = await sendNPS({
        phone: selectedContact.phone,
        contact_id: contactId,
        question,
        campaign_name: 'manual_chat'
      });

      // Adicionar mensagem NPS localmente para aparecer imediatamente
      if (response && typeof response === 'object' && 'nps_id' in response) {
        const npsMessageContent = {
          nps_data: {
            question: question,
            nps_id: response.nps_id,
            message_id: response.message_id,
            status: 'sent',
            campaign_name: 'manual_chat'
          }
        };

        // Enviar mensagem local do tipo NPS
        sendMessage(npsMessageContent, 'nps', `local_nps_${Date.now()}`);
      }

      console.log('NPS response:', response);

      // Atualizar a listagem de contatos
      setContacts((prev) => {
        const newContacts = new Map(prev);
        const c = newContacts.get(conversationKey(selectedContact));
        if (c) {
          c.lastMessage = '📊 Pesquisa NPS enviada';
          c.timestamp = new Date().toLocaleTimeString();
          c.timestampNumber = Date.now();
          applySentLastMessageMetadata(c, {
            providerMessageId: response?.message_id || null,
            sourceChannel: 'waha'
          });
          newContacts.set(conversationKey(selectedContact), c);
        }
        return newContacts;
      });

      console.log('NPS enviado com sucesso para:', selectedContact.phone);
    } catch (err) {
      console.error('Erro ao enviar NPS:', err);
      setSendError(parseApiError(err, 'Não foi possível enviar a pesquisa NPS.'));
    }
  };

  // Função para lidar com seleção de arquivos
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isInstagramContact(selectedContact)) {
      const input = event.target;
      const file = input.files?.[0];
      if (file && await handleInstagramFile(file)) input.value = '';
      return;
    }
    if (!event.target.files || event.target.files.length === 0 || !selectedContact) {
      return;
    }
    const contactId = requireSelectedContactId();
    if (!contactId) {
      event.target.value = '';
      return;
    }

    const file = event.target.files[0];
    event.target.value = ''; // reset

    // Ignorar arquivos de áudio - usamos apenas o gravador de áudio
    if (file.type.startsWith('audio/')) {
      console.log('Arquivos de áudio devem ser enviados usando o gravador de áudio.');
      return;
    }

    let fileType: 'image' | 'video';
    if (file.type.startsWith('image/')) {
      fileType = 'image';
    } else if (file.type.startsWith('video/')) {
      fileType = 'video';
    } else {
      console.error('Tipo de arquivo não suportado:', file.type);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      if (!e.target?.result) return;
      const base64Content = e.target.result.toString();

      const localMessageId = `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      let optimisticMessageCreated = false;
      try {
        setSendError(null);
        console.log(`[${fileType.toUpperCase()}] Arquivo selecionado:`, file.name);
        console.log(`[${fileType.toUpperCase()}] Tamanho:`, file.size, "bytes");
        console.log(`[${fileType.toUpperCase()}] Tipo MIME:`, file.type);
        console.log(`[${fileType.toUpperCase()}] Base64 gerado, tamanho:`, base64Content.length, "caracteres");
        console.log(`[${fileType.toUpperCase()}] Primeiros 100 chars:`, base64Content.substring(0, 100));

        console.log(`[${fileType.toUpperCase()}] ID da mensagem local:`, localMessageId);

        // Usar base64 diretamente em vez de blob URL
        const messageContent = {
          url: base64Content,  // Base64 direto, persiste no banco
          mimeType: file.type
        };

        // ✅ NOVO: Verificar se tem múltiplos providers
        if (whatsappProviders?.has_waha && whatsappProviders?.has_official) {
          console.log(`📱 [Providers] Clínica tem ambos providers - mostrando modal para ${fileType}`);
          setPendingMessage({
            type: fileType,
            content: messageContent,
            localMessageId
          });
          // ✅ NÃO mostrar mensagem ainda - aguardar seleção do provider no modal
          setShowProviderModal(true);
          return;
        }

        // ✅ NOVO: Se tem apenas API Oficial, usar ela
        if (!whatsappProviders?.has_waha && whatsappProviders?.has_official) {
          console.log(`📱 [Providers] Usando apenas API Oficial para ${fileType}`);
          await sendViaOfficial(fileType, messageContent, localMessageId);
          return;
        }

        // ✅ PADRÃO: Usar WAHA (mantém funcionamento atual intacto)
        console.log(`[${fileType.toUpperCase()}] Enviando mensagem para o hook sendMessage (WAHA)...`);
        sendMessage(messageContent, fileType, localMessageId);
        optimisticMessageCreated = true;

        // Enviar para o WhatsApp com base no tipo
        if (fileType === 'image') {
          const sendResult = await sendWhatsAppImage({
            phone: selectedContact.phone,
            image: base64Content,
            localMessageId: localMessageId,
            contact_id: contactId,
          });
          updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
        } else {
          const sendResult = await sendWhatsAppVideo({
            phone: selectedContact.phone,
            video: base64Content,
            localMessageId: localMessageId,
            contact_id: contactId,
          });
          updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
        }

        // Atualizar contato na lista
        setContacts((prev) => {
          const newContacts = new Map(prev);
          const c = newContacts.get(conversationKey(selectedContact));
          if (c) {
            c.lastMessage = `[${fileType.toUpperCase()}]`;
            c.timestamp = new Date().toLocaleTimeString();
            c.timestampNumber = Date.now();
            applySentLastMessageMetadata(c, { messageType: fileType, sourceChannel: 'waha' });
            newContacts.set(conversationKey(selectedContact), c);
          }
          return newContacts;
        });
      } catch (err) {
        console.error(`[handleFileChange] -> Erro ao enviar ${fileType}:`, err);
        if (optimisticMessageCreated) {
          updateMessageTracking(localMessageId, null, 'failed');
        }
        setSendError(parseApiError(err, `Não foi possível enviar ${fileType === 'image' ? 'a imagem' : 'o vídeo'}.`));
      }
    };

    reader.readAsDataURL(file);
  };

  // Função para carregar status dos fluxos do contato
  const loadContactFlowStatus = async (phone: string) => {
    if (!phone || phone.startsWith('contact:') || isInstagramContact(selectedContact)) return;
    const selectedKey = selectedConversationRef.current;
    try {
      setIsLoadingFlowStatus(true);
      const clinicId = localStorage.getItem('clinic_id');

      if (!clinicId) {
        console.error('clinicId não encontrado no localStorage');
        return;
      }

      const response = await api.get(
        `/api/flow-control/contact/${clinicId}/${encodeURIComponent(phone)}`,
      );
      if (selectedConversationRef.current === selectedKey) setContactFlowStatus(response.data.flows || {});
    } catch (error) {
      console.error('Erro ao carregar status dos fluxos:', error);
    } finally {
      if (selectedConversationRef.current === selectedKey) setIsLoadingFlowStatus(false);
    }
  };

  // Função para alternar fluxo específico
  const toggleContactFlow = async (flowType: string, isPaused: boolean, reason?: string) => {
    if (isInstagramContact(selectedContact)) return;
    try {
      const clinicId = localStorage.getItem('clinic_id');

      if (!clinicId || !selectedContact) {
        console.error('Dados necessários não encontrados:', { clinicId, selectedContact });
        return;
      }

      await api.post(`/api/flow-control/contact/${clinicId}/toggle`, {
          contact_identifier: selectedContact.phone,
          identifier_type: 'phone',
          flow_type: flowType,
          is_paused: isPaused,
          pause_reason: reason
      });
      await loadContactFlowStatus(selectedContact.phone);
    } catch (error) {
      console.error('Erro ao alternar fluxo:', error);
    }
  };

  const selectContactFromList = async (contact: Contact) => {
    // Normalizar o telefone antes de selecionar o contato
    const normalizedPhone = realContactPhone(contact);
    const contactKey = conversationKey(contact);
    if (!contactKey) return;

    console.log('🔍 Contato selecionado:', contact);
    console.log('📊 Flow Progress:', contact.flow_progress);
    console.log('📋 Tipo do contact:', typeof contact, 'É array?', Array.isArray(contact));
    console.log('🗂️ Chaves do contact:', Object.keys(contact));

    // Verificar se o contato existe no Map com o telefone normalizado
    const existingContact = contacts.get(contactKey);
    console.log('📱 Contato existente no Map:', existingContact);

    // IMPORTANTE: Resetar contador de mensagens não lidas para este contato
    setContacts((prev) => {
      const newMap = new Map(prev);
      if (prev.has(contactKey)) {
        const updatedContact = {
          ...prev.get(contactKey)!,
          unreadCount: 0, // Resetar contador ao selecionar o contato
        };
        newMap.set(contactKey, updatedContact);
      }
      return newMap;
    });

    let selectedContactData: Contact;
    if (existingContact) {
      console.log(`Selecionando contato existente: ${normalizedPhone}`);
      // Mesclar dados do contact (que tem flow_progress) com existingContact
      selectedContactData = {
        ...existingContact,
        ...contact, // Isso garante que flow_progress seja incluído
        phone: normalizedPhone,
        unreadCount: 0
      };
    } else {
      console.log(`Selecionando contato novo: ${normalizedPhone}`);
      selectedContactData = {
        ...contact,
        phone: normalizedPhone,
        unreadCount: 0,
      };
    }

    selectedContactData = withKnownContactMode(
      selectedContactData,
      contactModeValueRef.current,
    );

    setSelectedContact(selectedContactData);
    setSendError(null);

    (isInstagramContact(contact) ? markInstagramRead(contact.id) : markContactAsRead(normalizedPhone)).catch((err) => {
      console.error('Erro ao marcar contato como lido no backend:', err);
    });

    // 🔥 Carregar tags do contato selecionado
    const contactForTags = selectedContactData;
    console.log('🔍 SelectContact - Chamando loadContactTags para:', {
      contactId: contactForTags.id,
      contactName: contactForTags.name,
      contactPhone: contactForTags.phone,
      existingContact: !!existingContact,
      tagsFromObject: contactForTags.tags
    });
    loadContactTags(contactForTags);

  };

  const handleReviewDuplicateContacts = (
    identityError: ParsedApiError | null = sendError,
  ) => {
    const phone = identityError?.existingPhone || selectedContact?.phone;
    if (!phone) return;

    navigate('/contacts', {
      state: {
        searchTerm: phone,
        includeArchived: true,
        reviewDuplicates: true,
      },
    });
  };

  const handleOpenTemplateSendModal = () => {
    if (isInstagramContact(selectedContact)) return;
    if (!requireSelectedContactId()) return;
    setShowTemplateSendModal(true);
  };

  // Função para selecionar um novo contato do modal
  async function handleSelectNewContact(contact: Contact) {
    if (!conversationKey(contact)) return;
    // Marca o timestampNumber para aparecer na sidebar
    const updated = withKnownContactMode(
      { ...contact, phone: realContactPhone(contact), timestampNumber: Date.now() },
      contactModeValueRef.current,
    );

    // Atualiza o Map de contatos
    setContacts((prev) => {
      const newMap = new Map(prev);
      newMap.set(conversationKey(updated), updated);
      return newMap;
    });

    // Seleciona o contato
    setSelectedContact(updated);

    // Fecha o modal e limpa os dados do modal
    setShowNewChatModal(false);
    setModalContacts([]);
    setModalSearchTerm('');
    setModalOffset(0);
    setHasMoreModalContacts(true);
  }

  // Função para obter iniciais do nome
  const getInitials = (name: string): string => {
    if (!name || name.startsWith('+') || /^\d+$/.test(name.substring(0, 2))) {
      return 'CT';
    }

    const parts = name.split(' ').filter(part => part.length > 0);

    if (parts.length === 0) return 'CT';

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };


  // Lista ordenada de contatos (backend já fez a filtragem)
  const filteredAndSortedContacts = useMemo(() => {
    // Converter Map para array - backend já filtrou e ordenou
    let contactArray = Array.from(displayContacts.values());
    const hasSearch = normalizeContactSearchTerm(searchTerm).length > 0;

    // Na busca direta por telefone, também exibimos contatos sem histórico.
    const visibleContacts = hasSearch
      ? contactArray
      : contactArray.filter((contact) => contact.timestampNumber && contact.timestampNumber > 0);

    // Backend já aplicou filtros de busca e unread_only
    // Aqui só ordenamos localmente por timestamp como fallback
    visibleContacts.sort((a, b) => {
      const timeA = a.timestampNumber || 0;
      const timeB = b.timestampNumber || 0;
      return timeB - timeA;
    });

    return visibleContacts;
  }, [displayContacts, searchTerm]);

  useEffect(() => {
    if (contacts.size > 0 && filteredAndSortedContacts.length === 0) {
      console.warn('⚠️ Inconsistência detectada: contatos existem, mas lista filtrada está vazia!', {
        contactsSize: contacts.size
      });
    }
  }, [contacts.size, filteredAndSortedContacts.length]);

  // Controlar navegação mobile e carregar status dos fluxos
  useEffect(() => {
    if (selectedContact) {
      setShowMobileChat(true);
      // Carregar status dos fluxos quando selecionar contato
      if (!isInstagramContact(selectedContact)) loadContactFlowStatus(selectedContact.phone);
      else { setContactFlowStatus({}); setIsLoadingFlowStatus(false); }
    }
  }, [selectedContact]);

  // Sincroniza selectedContact com a versão mais recente do Map de contatos.
  // Necessário para refletir edição de lead e mudanças de funil carregadas em segundo plano.
  useEffect(() => {
    if (!selectedContact || !conversationKey(selectedContact)) return;
    const fresh = contacts.get(conversationKey(selectedContact)) ||
      Array.from(contacts.values()).find(contact => contact.id === selectedContact.id);
    if (!fresh) return;

    const freshId = Number(fresh.id || 0);
    const selectedId = Number(selectedContact.id || 0);
    const canPromoteId = selectedId <= 0 && Number.isInteger(freshId) && freshId > 0;
    const contactChanged =
      canPromoteId ||
      fresh.name !== selectedContact.name ||
      realContactPhone(fresh) !== selectedContact.phone ||
      fresh.instagram_identity_id !== selectedContact.instagram_identity_id ||
      fresh.instagram_username !== selectedContact.instagram_username ||
      (fresh.source_id ?? null) !== (selectedContact.source_id ?? null) ||
      (fresh.photo ?? '') !== (selectedContact.photo ?? '') ||
      (fresh.active_channel ?? null) !== (selectedContact.active_channel ?? null) ||
      fresh.human_mode !== selectedContact.human_mode ||
      fresh.funnel_stage !== selectedContact.funnel_stage ||
      fresh.flow_progress !== selectedContact.flow_progress ||
      fresh.lead_id !== selectedContact.lead_id ||
      fresh.possible_duplicate !== selectedContact.possible_duplicate ||
      fresh.duplicate_contact_id !== selectedContact.duplicate_contact_id ||
      fresh.funnel_status?.agendamento_id !== selectedContact.funnel_status?.agendamento_id ||
      fresh.funnel_status?.comparecimento_id !== selectedContact.funnel_status?.comparecimento_id ||
      fresh.funnel_status?.venda_id !== selectedContact.funnel_status?.venda_id ||
      fresh.funnel_status?.no_show_id !== selectedContact.funnel_status?.no_show_id;

    if (contactChanged) {
      console.log('[Chat] Contato mudou no Map, sincronizando selectedContact', {
        from: {
          name: selectedContact.name,
          source_id: selectedContact.source_id,
          stage: selectedContact.funnel_stage,
          status: selectedContact.funnel_status,
        },
        to: {
          name: fresh.name,
          source_id: fresh.source_id,
          stage: fresh.funnel_stage,
          status: fresh.funnel_status,
        },
      });
      setSelectedContact((prev) => prev ? {
        ...prev,
        id: canPromoteId ? freshId : prev.id,
        name: fresh.name,
        phone: realContactPhone(fresh),
        instagram_identity_id: fresh.instagram_identity_id,
        instagram_username: fresh.instagram_username,
        instagram_last_inbound_at: fresh.instagram_last_inbound_at,
        source_id: fresh.source_id,
        photo: fresh.photo,
        active_channel: fresh.active_channel,
        human_mode: fresh.human_mode,
        funnel_stage: fresh.funnel_stage,
        funnel_status: fresh.funnel_status,
        lead_id: fresh.lead_id,
        flow_progress: fresh.flow_progress,
        possible_duplicate: fresh.possible_duplicate,
        duplicate_contact_id: fresh.duplicate_contact_id,
      } : prev);
    }
  }, [contacts, selectedContact]);

  // Contatos recebidos primeiro pelo WebSocket nascem com id=0. Só promovemos
  // essa identidade quando as variantes do telefone apontam para um único
  // cadastro ativo. Equivalentes arquivados continuam sinalizados para revisão.
  // Um contato que já possui id positivo nunca é substituído silenciosamente.
  useEffect(() => {
    const selectedPhone = selectedContact && !isInstagramContact(selectedContact) ? realContactPhone(selectedContact) : '';
    const currentId = Number(selectedContact?.id || 0);

    if (!selectedPhone || (Number.isInteger(currentId) && currentId > 0)) {
      setIsResolvingSelectedContact(false);
      return;
    }

    const mappedReadyContact = contactsRef.current.get(selectedPhone);
    const mappedReadyId = Number(mappedReadyContact?.id || 0);
    if (mappedReadyContact && Number.isInteger(mappedReadyId) && mappedReadyId > 0) {
      setSelectedContact((currentContact) => {
        if (
          !currentContact ||
          Number(currentContact.id || 0) > 0 ||
          String(currentContact.phone || '').replace(/\D/g, '') !== selectedPhone
        ) {
          return currentContact;
        }
        return {
          ...mappedReadyContact,
          ...currentContact,
          id: mappedReadyId,
          phone: selectedPhone,
        };
      });
      setIsResolvingSelectedContact(false);
      return;
    }

    let disposed = false;
    let requestInFlight = false;

    const setIdentityConflict = (
      message: string,
      existingContactId?: number,
    ) => {
      setSendError((currentError) => {
        if (
          currentError?.code === 'whatsapp_identity_conflict' &&
          currentError.existingPhone === selectedPhone &&
          currentError.existingContactId === existingContactId &&
          currentError.message === message
        ) {
          return currentError;
        }
        return {
          code: 'whatsapp_identity_conflict',
          message,
          existingContactId,
          existingPhone: selectedPhone,
        };
      });
    };

    const resolveTemporaryIdentity = async () => {
      if (requestInFlight || disposed) return;
      requestInFlight = true;
      setIsResolvingSelectedContact(true);
      try {
        const response = await getContacts({
          search: selectedPhone,
          show_archived: true,
          lightweight: true,
          limit: 500,
        });
        if (disposed) return;

        const selectedVariants = getWhatsAppIdentityVariants(selectedPhone);
        const relatedContacts = response.contacts.filter((contact) => {
          const contactVariants = getWhatsAppIdentityVariants(contact.phone);
          return Array.from(contactVariants).some((variant) =>
            selectedVariants.has(variant),
          );
        });
        const activeMatches = relatedContacts.filter(
          (contact) => contact.archived !== true,
        );
        const archivedMatches = relatedContacts.filter(
          (contact) => contact.archived === true,
        );
        const candidate = activeMatches.length === 1 ? activeMatches[0] : null;

        if (response.has_more || activeMatches.length > 1) {
          setIdentityConflict(
            'Há mais de um cadastro ativo equivalente para este telefone. Revise os cadastros antes de enviar.',
          );
          return;
        }

        if (!candidate && archivedMatches.length > 0) {
          setIdentityConflict(
            'Foram encontrados apenas cadastros arquivados para este telefone. Revise os cadastros antes de enviar.',
            archivedMatches[0].id,
          );
          return;
        }

        if (!candidate || !Number.isInteger(candidate.id) || candidate.id <= 0) {
          return;
        }

        if (candidate.possible_duplicate && relatedContacts.length === 1) {
          setIdentityConflict(
            'Existe outro cadastro equivalente que não pôde ser reconciliado com segurança. Revise os cadastros antes de enviar.',
            candidate.id,
          );
          return;
        }

        const mappedContact = contactsRef.current.get(selectedPhone);
        if (
          mappedContact &&
          mappedContact.id > 0 &&
          mappedContact.id !== candidate.id
        ) {
          setIdentityConflict(
            'A conversa aponta para outro cadastro salvo. Revise os cadastros antes de enviar.',
            mappedContact.id,
          );
          return;
        }

        const promotedContact: Contact = {
          ...candidate,
          ...mappedContact,
          id: candidate.id,
          phone: selectedPhone,
          name: candidate.name || mappedContact?.name || selectedPhone,
          archived: candidate.archived,
          possible_duplicate: candidate.possible_duplicate,
          duplicate_contact_id: candidate.duplicate_contact_id,
        };

        setContacts((previousContacts) => {
          const existing = previousContacts.get(selectedPhone);
          if (existing && existing.id > 0 && existing.id !== candidate.id) {
            return previousContacts;
          }
          const nextContacts = new Map(previousContacts);
          nextContacts.set(selectedPhone, {
            ...promotedContact,
            ...existing,
            id: candidate.id,
            phone: selectedPhone,
            archived: candidate.archived,
            possible_duplicate: candidate.possible_duplicate,
            duplicate_contact_id: candidate.duplicate_contact_id,
          });
          contactsRef.current = nextContacts;
          return nextContacts;
        });

        setSelectedContact((currentContact) => {
          if (
            !currentContact ||
            currentContact.id > 0 ||
            String(currentContact.phone || '').replace(/\D/g, '') !== selectedPhone
          ) {
            return currentContact;
          }
          return {
            ...promotedContact,
            ...currentContact,
            id: candidate.id,
            phone: selectedPhone,
            archived: candidate.archived,
            possible_duplicate: candidate.possible_duplicate,
            duplicate_contact_id: candidate.duplicate_contact_id,
          };
        });
        setSendError((currentError) =>
          currentError?.code === 'contact_identity_pending' ||
          currentError?.code === 'whatsapp_identity_conflict'
            ? null
            : currentError,
        );
      } catch {
        // A resolução é repetida em segundo plano; o estado visual já informa
        // que o contato ainda não possui uma identidade segura.
      } finally {
        requestInFlight = false;
        if (!disposed) {
          setIsResolvingSelectedContact(false);
        }
      }
    };

    void resolveTemporaryIdentity();
    const intervalId = window.setInterval(resolveTemporaryIdentity, 5000);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [selectedContact?.id, selectedContact?.phone, selectedContact?.active_channel]);

  // Função para voltar à lista no mobile
  const handleBackToList = () => {
    setShowMobileChat(false);
    setSelectedContact(null);
  };

  // Mensagens agrupadas por dia
  const processedMessages = groupMessagesByDay(messages);

  // Renderização personalizada para mensagens e separadores de data
  const renderCustomMessage = (message: OptimizedMessage) => {
    const isOwn = message.fromMe;

    // DEBUG: Logar isOwn para verificar se está correto
    if (message.content === 'oi' || (typeof message.content === 'string' && message.content.includes('tudo bem'))) {
      console.log(`[Chat.tsx] renderCustomMessage: ID=${message.id}, content='${message.content}', fromMe=${message.fromMe}, isOwn=${isOwn}`);
    }

    // Renderiza o conteúdo da mensagem com base no tipo
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
        {/* Avatar para mensagens recebidas */}
        {!isOwn && (
          <div className="flex-shrink-0 mr-2">
            <ContactAvatar photo={message.sender?.photo} name={message.sender?.name} className="w-8 h-8 rounded-full" iconClassName="w-4 h-4" />
          </div>
        )}

        {/* Conteúdo da mensagem */}
        <div className="max-w-[70%]">
          {/* Usar OptimizedMessageContent para texto, imagem e vídeo */}
          {message.type !== 'audio' ? (
            <OptimizedMessageContent message={message} isOwn={isOwn} />
          ) : (
            // Usar AudioWaveform para mensagens de áudio
            <div className={`rounded-2xl p-2 ${isOwn ? 'bg-sminy-primary' : 'bg-gray-100'}`}>
              <AudioWaveform
                src={
                  typeof message.content === 'string'
                    ? message.content
                    : (message.content as any).url || ''
                }
                duration={(message.content as any).duration}  // <--- Passe aqui
                messageColor={isOwn ? 'light' : 'dark'}
                className="w-64"
              />
            </div>
          )}
        </div>

        {/* Avatar para mensagens enviadas (opcional) */}
        {isOwn && (
          <div className="flex-shrink-0 ml-2">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
              {clinicInfo.logo_url ? (
                <img
                  src={clinicInfo.logo_url}
                  alt={clinicInfo.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-500 to-teal-600 text-white font-medium">
                  {getInitials(clinicInfo.name)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Implementação personalizada do VirtualizedMessageList para integrar DateSeparator
  const renderMessagesList = () => {
    if (isLoading && messages.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sminy-primary"></div>
        </div>
      );
    }

    return (
      <div className="px-4 py-6 overflow-y-auto h-full">
        {/* Botão de carregar mais mensagens */}
        {hasMore && (
          <div className="flex justify-center mb-4">
            <button
              onClick={loadMoreMessages}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-600 transition-colors"
            >
              Carregar mensagens anteriores
            </button>
          </div>
        )}

        {/* Renderizar mensagens agrupadas por dia */}
        {processedMessages.map((group) => (
          <div key={group.dayLabel}>
            {/* Separador de data */}
            <DateSeparator date={group.dayLabel} />

            {/* Mensagens do dia */}
            <div className="space-y-3">
              {group.messages.map((message) => (
                <div key={message.id}>
                  {renderCustomMessage(message)}
                </div>
              ))}
            </div>
          </div>
        ))}

        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <p className="text-gray-500 text-sm">
              Nenhuma mensagem para exibir
            </p>
          </div>
        )}
      </div>
    );
  };

  // Se for mobile, renderiza o componente otimizado
  if (isMobile) {
    return <ChatMobile />;
  }

  // Renderização para desktop
  return (
    <div className="h-[calc(100vh-5rem)] sm:h-screen flex flex-col overflow-hidden">
      {/* Barra de status de conexão 
      <div className="bg-white border-b border-gray-200 py-1 px-4">
        <ConnectionStatusIndicator />
      </div>*/}

      <div className="flex flex-1 min-h-0 relative">
        {/* Sidebar de contatos extraída para components/Chat/ChatSidebar */}
        <ChatSidebar
          showMobileChat={showMobileChat}
          showArchivedSection={attendanceEnabled ? conversationState === 'archived' : showArchivedSection}
          attendanceLoading={attendanceEnabled === null}
          attendanceEnabled={attendanceEnabled === true}
          conversationState={attendanceEnabled !== false ? conversationState : undefined}
          onConversationStateChange={setConversationState}
          assignmentFilter={assignmentFilter}
          onAssignmentFilterChange={setAssignmentFilter}
          isContactScopeRestricted={isContactScopeRestricted}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showUnreadOnly={showUnreadOnly}
          onToggleUnread={setShowUnreadOnly}
          totalUnreadCount={getTotalUnreadCount()}
          contactFilters={contactFilters}
          onFiltersChange={setContactFilters}
          contacts={filteredAndSortedContacts}
          selectedConversationKey={conversationKey(selectedContact) || null}
          selectedContactPresenceActivity={contactPresence.activity}
          isContactsLoading={isContactsLoading}
          isLoadingMoreContacts={isLoadingMoreContacts}
          hasMoreContacts={hasMoreContacts}
          isThrottled={isThrottled}
          currentOffset={currentOffset}
          pageSize={CONTACTS_PAGE_SIZE}
          formatTimestamp={formatChatTimestamp}
          onNewChat={() => setShowNewChatModal(true)}
          onSelectContact={selectContactFromList}
          onArchiveToggle={handleArchiveToggle}
          isArchiving={isArchiving}
          onEnterArchivedSection={() => {
            setShowArchivedSection(true);
            setSelectedContact(null);
            setSearchTerm('');
            setContactFilters({ funnelStages: [], activeFlows: [] });
          }}
          onLeaveArchivedSection={() => {
            setShowArchivedSection(false);
            setSelectedContact(null);
          }}
          onTaskClick={(c) => {
            setSelectedContact(withKnownContactMode(c, contactModeValueRef.current));
            setContactSidebarTab('tasks');
            setShowContactSidebar(true);
          }}
          onLoadMore={(offset, limit, append) => loadContacts(offset, limit, append)}
          setIsThrottled={setIsThrottled}
        />

        {/* Main Chat Area */}
        <main className={`
          w-full lg:flex-1 flex flex-col min-w-0 overflow-hidden
          lg:flex
          ${showMobileChat ? 'flex' : 'hidden'}
        `}>
          {selectedContact ? (
            <>
              {/* Chat Header */}
              <ChatHeader
                attendanceEnabled={attendanceEnabled === true}
                attendanceAction={<div ref={attendanceCloseTarget} />}
                contact={selectedContactDisplay || selectedContact}
                windowStatus={windowStatus}
                contactFlowStatus={contactFlowStatus}
                presence={contactPresence}
                onBackToList={handleBackToList}
                onShowTemplateSendModal={handleOpenTemplateSendModal}
                onOpenPanel={() => {
                  setContactSidebarTab('timeline');
                  setShowContactSidebar(true);
                }}
              />

      {attendanceEnabled !== false && selectedContact && <ContactAttendance key={selectedContact.id} enabled={attendanceEnabled} renderModeControl={(humanMode, onAssume, onModeChanged, busy) => <ContactModeControl contactId={selectedContact.id} contactLabel={selectedContact.name || selectedContact.phone} humanMode={humanMode} identityResolved={isSelectedContactIdentityResolved && !busy} onAssume={onAssume} onModeConfirmed={response => { const accepted = handleContactModeConfirmed(response); if (accepted !== false) onModeChanged(response.human_mode === true); return accepted; }} variant="compact" />} claimTarget={attendanceClaimTarget} closeTarget={attendanceCloseTarget} contactId={selectedContact.id} onChanged={(action, data) => { setAttendanceRevision(value => value + 1); if (action === 'release' || action === 'transfer' || action === 'close') { setSelectedContact(null); } else { setSelectedContact(current => current ? { ...current, human_mode: data.human_mode } : current); } }} />}
              {/* Messages Area - Versão otimizada com virtualização */}
              {selectedContact ? (
                <div className="flex-1 overflow-y-auto bg-gray-50">
                  {isLoading ? (
                    // Estado de carregamento das mensagens com esqueletos
                    <div className="p-2 lg:p-4 space-y-8">
                      {/* Data separator skeleton */}
                      <div className="flex justify-center my-4">
                        <div className="bg-gray-200 animate-pulse rounded-full w-24 h-5"></div>
                      </div>

                      {/* Mensagens recebidas (esquerda) */}
                      <div className="space-y-4">
                        {/* Primeira mensagem recebida */}
                        <div className="flex items-start animate-pulse">
                          <div className="w-8 h-8 rounded-full bg-gray-200 mr-2"></div>
                          <div className="max-w-[70%]">
                            <div className="bg-gray-200 rounded-2xl p-3 w-48 h-16"></div>
                            <div className="mt-1 w-12 h-3 bg-gray-200 rounded float-right"></div>
                          </div>
                        </div>

                        {/* Segunda mensagem recebida */}
                        <div className="flex items-start animate-pulse">
                          <div className="w-8 h-8 rounded-full bg-gray-200 mr-2"></div>
                          <div className="max-w-[70%]">
                            <div className="bg-gray-200 rounded-2xl p-3 w-64 h-12"></div>
                            <div className="mt-1 w-12 h-3 bg-gray-200 rounded float-right"></div>
                          </div>
                        </div>
                      </div>

                      {/* Mensagens enviadas (direita) */}
                      <div className="space-y-4">
                        {/* Primeira mensagem enviada */}
                        <div className="flex items-start justify-end animate-pulse">
                          <div className="max-w-[70%]">
                            <div className="bg-sminy-primary/20 rounded-2xl p-3 w-40 h-10"></div>
                            <div className="mt-1 w-12 h-3 bg-gray-200 rounded float-right"></div>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-gray-200 ml-2"></div>
                        </div>

                        {/* Segunda mensagem enviada */}
                        <div className="flex items-start justify-end animate-pulse">
                          <div className="max-w-[70%]">
                            <div className="bg-sminy-primary/20 rounded-2xl p-3 w-56 h-20"></div>
                            <div className="mt-1 w-12 h-3 bg-gray-200 rounded float-right"></div>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-gray-200 ml-2"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Componente VirtualizedMessageList quando as mensagens estão carregadas
                    <VirtualizedMessageList
                      messages={messages}
                      isLoading={isLoading}
                      isLoadingMore={isLoadingMore}
                      hasMore={hasMore}
                      onLoadMore={loadMoreMessages}
                      onReply={selectedIsInstagram ? undefined : handleReplyToMessage}
                      instagramScope={selectedIsInstagram && instagramConversation.identity ? { contactId: selectedContactId, identityId: instagramConversation.identity.identity_id } : undefined}
                      canReplyComment={instagramComments.canReply && !instagramConversation.sending}
                      onReplyComment={instagramComments.select}
                      onReact={selectedIsInstagram ? undefined : handleReactToMessage}
                      onCorrectAgentMessage={message => { setCorrectionMessage(message); setCorrectionContactId(selectedContactId); setCorrectionClinicId(activeClinicId()); }}
                      canManageLearning={hasPermission('prompt') && isSelectedContactIdentityResolved && !selectedIsInstagram}
                      contactId={selectedContactId}
                      endAdornment={(
                        <ContactPresenceBubble activity={contactPresence.activity} />
                      )}
                      endAdornmentKey={contactPresence.activity}
                    />
                  )}
                </div>
              ) : (
                // Estado quando nenhum contato está selecionado
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  Selecione uma conversa para começar
                </div>
              )}

              {/* Input Area - Componente otimizado com botão de modo humano */}
              <div className="flex-shrink-0 relative w-full">
                {!isSelectedContactIdentityResolved && !selectedContact.archived && (
                  <div
                    role="status"
                    className={`flex flex-col gap-2 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between ${
                      isDark
                        ? 'border-amber-800 bg-amber-950/40 text-amber-200'
                        : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}
                  >
                    <div className="flex min-w-0 items-start gap-2">
                      {isResolvingSelectedContact ? (
                        <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin" />
                      ) : (
                        <Timer className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      )}
                      <span>
                        {sendError?.code === 'whatsapp_identity_conflict'
                            ? 'Este telefone precisa de revisão antes de qualquer envio.'
                            : 'Contato recém-recebido ainda está sincronizando. Aguarde a identificação segura antes de enviar.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleReviewDuplicateContacts(null)}
                      className="flex-shrink-0 self-start font-semibold text-sminy-primary hover:underline sm:self-auto"
                    >
                      Revisar cadastros
                    </button>
                  </div>
                )}
                {sendError && sendError.code !== 'archived_contact' && (
                  <WhatsAppIdentityErrorAlert
                    error={sendError}
                    variant="top-bar"
                    onOpenExisting={() => handleReviewDuplicateContacts()}
                    existingActionLabel="Revisar cadastros duplicados"
                    showExistingAction={
                      Boolean(sendError.existingContactId) ||
                      sendError.code === 'whatsapp_identity_conflict'
                    }
                  />
                )}
                {replyTarget && (
                  <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-2">
                    <div className="min-w-0 border-l-4 border-sminy-primary pl-3">
                      <div className="text-xs font-semibold text-sminy-primary truncate">
                        {getMessageSenderLabel(replyTarget)}
                      </div>
                      <div className="text-xs text-gray-600 truncate">
                        {getReplyPreviewContent(replyTarget)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyTarget(null)}
                      className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      title="Cancelar resposta"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {selectedIsInstagram && instagramComments.target ? <InstagramCommentComposer state={instagramComments} /> : <>
                {selectedIsInstagram && <p className={`px-4 pt-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Direct · mensagem privada</p>}
                <OptimizedMessageInput
                  onSendText={handleSendMessage}
                  onSendImage={(file) => {
                    if (fileInputRef.current) {
                      fileInputRef.current.files = ((): FileList => {
                        const dataTransfer = new DataTransfer();
                        dataTransfer.items.add(file);
                        return dataTransfer.files;
                      })();
                      handleFileChange({ target: fileInputRef.current } as any);
                    }
                  }}
                  onSendVideo={(file) => {
                    if (fileInputRef.current) {
                      fileInputRef.current.files = ((): FileList => {
                        const dataTransfer = new DataTransfer();
                        dataTransfer.items.add(file);
                        return dataTransfer.files;
                      })();
                      handleFileChange({ target: fileInputRef.current } as any);
                    }
                  }}
                  onSendAudio={handleAudioRecorded}
                  onSendNPS={selectedIsInstagram ? undefined : handleSendNPS}
                  onRecordingStateChange={selectedIsInstagram ? undefined : contactPresence.onRecordingStateChanged}
                  recordingContextKey={conversationKey(selectedContact)}
                  preserveDraftOnSubmit={selectedIsInstagram}
                  mediaDisabled={selectedIsInstagram && !instagramCanSendMedia}
                  textDisabled={selectedIsInstagram && !instagramCanSendText}
                  disabled={!isSelectedContactIdentityResolved || (selectedIsInstagram && (!instagramReady || !instagramWindowOpen))}
                  placeholder={
                    selectedIsInstagram && !instagramCanSendText
                      ? instagramConversation.loading ? 'Verificando conversa...' : instagramConversation.sending ? 'Enviando mensagem...' : instagramConversation.error || (instagramConversation.identity && !instagramWindowOpen ? 'Aguarde uma nova mensagem para responder.' : 'Envio indisponível nesta conversa.')
                      : isSelectedContactIdentityResolved
                      ? 'Digite sua mensagem...'
                      : 'Aguardando identificação segura do contato...'
                  }
                  value={newMessage}
                  onChange={(value) => {
                    setNewMessage(value);
                    if (!selectedIsInstagram) contactPresence.onTextChanged(value);
                  }}
                  onTextBlur={selectedIsInstagram ? undefined : contactPresence.onTextBlur}
                  isWindowExpired={!!(selectedContact.active_channel === 'official_whatsapp' && windowStatus && !windowStatus.is_active && !whatsappProviders?.has_waha)}
                  onSendTemplate={selectedIsInstagram ? undefined : handleOpenTemplateSendModal}
                  extraControl={
                    <><div ref={attendanceClaimTarget} />{attendanceEnabled === false && <ContactModeControl

                      contactId={selectedContact.id}
                      contactLabel={selectedContact.name || selectedContact.phone}
                      humanMode={selectedContact.human_mode === true}
                      identityResolved={isSelectedContactIdentityResolved}
                      onModeConfirmed={handleContactModeConfirmed}
                      variant="compact"
                    />}</>
                  }
                />
                </>}

                {/* INPUT FILE INVISÍVEL - mantido para compatibilidade */}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept="image/*,video/*"
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 p-4">
              <p className="text-center">Selecione uma conversa para começar</p>
            </div>
          )}
        </main>

        {/* Modal de contatos */}
        {showNewChatModal && (
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200 p-4"
            onClick={() => setShowNewChatModal(false)}
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden transform transition-all duration-200 animate-in slide-in-from-bottom-4 zoom-in-95"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold tracking-tight text-gray-800">
                  Selecione um contato
                </h3>
                <button
                  onClick={() => setShowNewChatModal(false)}
                  title="Fechar"
                  className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 max-h-[70vh] overflow-y-auto">
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Buscar contatos..."
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-sminy-primary/30 focus:border-sminy-primary focus:bg-white"
                    value={modalSearchTerm}
                    onChange={(e) => setModalSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>

                <div
                  className="space-y-1 max-h-80 overflow-y-auto"
                  onScroll={(e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                    const nearBottom = scrollHeight - scrollTop - clientHeight < 50;
                    if (nearBottom && hasMoreModalContacts && !isLoadingModalContacts) {
                      loadModalContacts(modalOffset, 20, true);
                    }
                  }}
                >
                  {isLoadingModalContacts && modalContacts.length === 0 ? (
                    // Skeleton inicial
                    <div className="space-y-1">
                      {Array(5).fill(0).map((_, index) => (
                        <div key={index} className="w-full p-3 rounded-xl flex items-center gap-3 animate-pulse">
                          <div className="w-10 h-10 rounded-full bg-gray-200" />
                          <div className="flex-1 space-y-2">
                            <div className="h-3.5 bg-gray-200 rounded w-1/2" />
                            <div className="h-3 bg-gray-200 rounded w-3/4" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : modalContacts.length === 0 ? (
                    // Empty state DS
                    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                        <MessageSquare className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-800 mb-1">
                        Nenhum contato encontrado
                      </h3>
                      <p className="text-xs text-gray-500 max-w-xs">
                        Tente outro termo de busca.
                      </p>
                    </div>
                  ) : (
                    modalContacts.map((contact) => (
                      <button
                        key={contact.phone}
                        onClick={() => handleSelectNewContact(contact)}
                        className="w-full text-left p-3 rounded-xl hover:bg-sminy-primary/5 flex items-center gap-3 transition-colors"
                      >
                        <ContactAvatar photo={contact.photo} name={contact.name || contact.phone} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{contact.name}</p>
                          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
                            <p className="text-xs text-gray-500 font-mono tracking-tight truncate">
                              {formatBrazilianPhone(contact.phone)}
                            </p>
                            {contact.possible_duplicate && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                                Possível duplicidade
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}

                  {/* Carregando mais (spinner DS) */}
                  {isLoadingModalContacts && modalContacts.length > 0 && (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-sminy-primary" />
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end px-5 py-4 border-t border-gray-200 bg-gray-50/50">
                <button
                  onClick={() => setShowNewChatModal(false)}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Painel lateral unificado do contato (timeline + tarefas + anotações + fluxos) */}
        {selectedContact && (
          <ContactSidebar
            attendanceEnabled={attendanceEnabled === true}
            isOpen={showContactSidebar}
            onClose={() => setShowContactSidebar(false)}
            contactId={selectedContact.id}
            contactPhone={selectedContact.phone}
            contactName={selectedContact.name}
            contactPhoto={selectedContact.photo}
            contactSourceId={selectedContact.source_id}
            clinicId={parseInt(localStorage.getItem('clinic_id') || '1')}
            humanMode={selectedContact.human_mode === true}
            identityResolved={isSelectedContactIdentityResolved}
            onModeConfirmed={handleContactModeConfirmed}
            initialTab={contactSidebarTab}
            timelineRefreshToken={timelineRefreshToken}
            flowsEnabled={!selectedIsInstagram}
            contactFlowStatus={contactFlowStatus}
            isLoadingFlowStatus={isLoadingFlowStatus}
            flowProgress={selectedContact.flow_progress}
            onToggleFlow={toggleContactFlow}
            pendingTasksCount={selectedContact.pending_tasks_count}
            hasFlowAlert={Object.values(contactFlowStatus).some((f: any) => f?.is_paused)}
            funnelStage={selectedContact.funnel_stage}
            funnelStatus={selectedContact.funnel_status}
            leadId={selectedContact.lead_id}
            onLeadUpdated={(updated) => {
              console.log('[Chat] Lead atualizado, propagando para selectedContact e lista', updated);
              const newName = updated.name || selectedContact.name;
              const newSourceId = updated.source_id ?? selectedContact.source_id;
              // Quando é criação (selectedContact não tinha lead_id), promove para 'lead'
              const wasContact = !selectedContact.lead_id;
              const newLeadId = updated.id ?? selectedContact.lead_id;
              const newFunnelStage = wasContact ? 'lead' : selectedContact.funnel_stage;

              setSelectedContact((prev) => prev ? {
                ...prev,
                name: newName,
                source_id: newSourceId,
                lead_id: newLeadId,
                funnel_stage: newFunnelStage,
              } : prev);
              setContacts((prev) => {
                const next = new Map(prev);
                const existing = next.get(conversationKey(selectedContact));
                if (existing) {
                  next.set(conversationKey(selectedContact), {
                    ...existing,
                    name: newName,
                    source_id: newSourceId,
                    lead_id: newLeadId,
                    funnel_stage: newFunnelStage,
                  });
                }
                contactsRef.current = next;
                return next;
              });
            }}
            onFunnelChanged={() => {
              console.log('[Chat] Funil mudou, recarregando contatos');
              loadContacts(0, CONTACTS_PAGE_SIZE, false);
            }}
            contactTags={contactTags}
            tagsLoading={tagsLoading}
            onUpdateTags={updateContactTags}
          />
        )}

        {/* ✅ NOVO: Modal de seleção de provider (WAHA vs API Oficial) */}
        {selectedContact && !selectedIsInstagram && isSelectedContactIdentityResolved && (
          <ProviderSelectionModal
            isOpen={showProviderModal}
            onClose={() => {
              setShowProviderModal(false);
              setPendingMessage(null);
            }}
            onSelectProvider={handleProviderSelected}
            windowStatus={windowStatus}
            wahaSession={whatsappProviders?.waha_session || 'Business'}
            officialPhoneId={whatsappProviders?.official_phone_id || 'Oficial'}
            disableOfficial={pendingMessage?.content === 'nps'}
            disableReason={
              pendingMessage?.content === 'nps'
                ? 'Pesquisas NPS só podem ser enviadas via WhatsApp Business. A API Oficial do WhatsApp não suporta enquetes interativas.'
                : undefined
            }
            onSendTemplate={() => {
              setShowProviderModal(false);
              setPendingMessage(null);
              handleOpenTemplateSendModal();
            }}
          />
        )}
        {correctionMessage && correctionContactId === selectedContactId && correctionClinicId === activeClinicId() && selectedContact && isSelectedContactIdentityResolved && hasPermission('prompt') && correctionClinicId && (
          <AgentCorrectionDialog
            key={`${selectedContactId}:${correctionMessage.id}`}
            clinicId={correctionClinicId}
            message={correctionMessage}
            onClose={() => setCorrectionMessage(null)}
            onPublished={() => { setCorrectionMessage(null); if (correctionClinicId === activeClinicId()) setCorrectionPublished(true); }}
          />
        )}
        {correctionPublished && <div role="status" className={`fixed bottom-6 right-4 z-50 flex items-center gap-3 rounded-xl border border-sminy-primary/20 px-4 py-3 text-sm shadow-xl ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-800'}`}><span>Correção publicada na biblioteca da clínica.</span><button type="button" onClick={() => setCorrectionPublished(false)} className="min-h-11 rounded-xl px-2 text-sminy-primary">Fechar</button></div>}

        {/* ✅ NOVO: Modal de envio de templates (fora da janela de 24h) */}
        {selectedContact && !selectedIsInstagram && isSelectedContactIdentityResolved && (
          <TemplateSendModal
            isOpen={showTemplateSendModal}
            onClose={() => setShowTemplateSendModal(false)}
            clinicId={Number(localStorage.getItem('clinic_id'))}
            contactId={selectedContact.id}
            contactPhone={selectedContact.phone}
            contactName={selectedContact.name}
            onSuccess={(response) => {
              console.log('Template enviado com sucesso:', response);
              // O modal já adiciona a mensagem via Redis/WebSocket
            }}
            onSelectExistingContact={(identityError) => {
              setShowTemplateSendModal(false);
              setSendError(identityError);
              handleReviewDuplicateContacts(identityError);
            }}
          />
        )}
      </div>
    </div>

  );
};

export default ChatPage5;
